/*
 * Step 5 (Configuration — Variants) reads/writes the relational variant_groups/
 * variant_options tables (see supabase/migrations/20260724000000_add_variant_groups_and_options.sql
 * and 20260724000004_backfill_variant_groups_and_options.sql) rather than PATCHing
 * products.variants directly. products.variants is a generated column, kept in sync by a
 * trigger (20260724000001_add_variant_jsonb_generator.sql) as a side effect of writes to
 * those tables — the mobile app still reads products.variants directly.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  clearanceOriginal,
  extractSpecSheet,
  formatClearanceOriginal,
  type ConsumerSpecs,
  type InstallerSpecs,
  type Clearances,
} from "@/lib/specReader";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";
import {
  DEFAULT_PERGOLA_PARAMS, PERGOLA_PARAM_FIELDS, validatePergolaParams, toPergolaDimensions,
  type PergolaParams,
} from "@/lib/pergolaParams";
import {
  PARAMETRIC_SKIPPED_STEPS, nextStepFrom as nextApplicableStep,
  prevStepFrom as prevApplicableStep, type ProductType,
} from "@/lib/wizardSteps";

import { supabase } from "@/lib/supabase/client";
import Papa from "papaparse";
import { isAssetPipelineFinished } from "@/lib/assetStatus";

/* ------------------------------------------------------------------ */
/* Constants                                                            */
/* ------------------------------------------------------------------ */

const SB_URL = import.meta.env.VITE_SUPABASE_URL as string;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

const CATEGORY_GROUPS = [
  {
    label: "Hearth",
    options: [
      { value: "gas_fireplace",       label: "Gas Fireplace / Insert" },
      { value: "wood_fireplace",      label: "Wood Fireplace / Stove" },
      { value: "pellet_stove",        label: "Pellet Stove / Insert" },
      { value: "electric_fireplace",  label: "Electric Fireplace" },
      { value: "fireplace_insert",    label: "Fireplace Insert" },
      { value: "gas_logs",            label: "Gas Logs & Burners" },
      { value: "chimney_venting",     label: "Chimney & Venting" },
      { value: "fireplace_accessory", label: "Hearth Accessory" },
    ],
  },
  {
    label: "Patio & Outdoor",
    options: [
      { value: "patio_furniture",   label: "Patio Furniture" },
      { value: "outdoor_kitchen",   label: "Outdoor Kitchen" },
      { value: "pergola_gazebo",    label: "Pergola / Gazebo" },
      { value: "fire_pit",          label: "Fire Pit / Bowl" },
      { value: "patio_heater",      label: "Patio Heater" },
      { value: "outdoor_accessory", label: "Outdoor Accessory" },
    ],
  },
  {
    label: "Barbecue",
    options: [
      { value: "gas_grill",       label: "Gas Grill" },
      { value: "charcoal_grill",  label: "Charcoal Grill" },
      { value: "pellet_grill",    label: "Pellet Grill / Smoker" },
      { value: "kamado_grill",    label: "Kamado Grill" },
      { value: "built_in_grill",  label: "Built-in Grill" },
      { value: "bbq_accessory",   label: "BBQ Accessory" },
    ],
  },
];

const ALL_CATEGORIES: { value: string; label: string }[] = CATEGORY_GROUPS.flatMap((g) => g.options);

function categoryLabel(value: string) {
  return ALL_CATEGORIES.find((o) => o.value === value)?.label ?? value;
}

// Categories where burner-type/interior-panel variant configuration is offered, in
// addition to the always-available exterior-finish group. Confirmed with product owner
// 2026-07-23 — see docs/superpowers/specs/2026-07-23-variant-groups-and-state-availability-design.md.
const BURNER_INTERIOR_GATE_CATEGORIES = new Set([
  "gas_fireplace", "wood_fireplace", "pellet_stove", "fireplace_insert",
]);

const TEXTURE_MAPS = [
  {
    key: "diffuse" as const,
    label: "Diffuse Map",
    tip: "Color and pattern of the surface (the main texture)",
  },
  {
    key: "normal" as const,
    label: "Normal Map",
    tip: "Encodes surface bumps and fine detail without extra geometry",
  },
  {
    key: "roughness" as const,
    label: "Roughness Map",
    tip: "Controls how shiny vs matte the surface appears",
  },
  {
    key: "metalness" as const,
    label: "Metalness Map",
    tip: "Marks which areas are metallic (white) vs non-metallic (black)",
  },
] as const;

type TextureKey = (typeof TEXTURE_MAPS)[number]["key"];

const PHOTO_ANGLES = ["Front", "Back", "Left", "Right", "3/4 Left", "3/4 Right", "Top", "Detail"] as const;

// ─── Variant types (coordinated with mobile VariantsConfig) ──────────────────

type VariantGroupType = "exterior_finish" | "burner_type" | "interior_panel";

interface VariantOption {
  id: string;
  label: string;
  swatch_url: string | null;
  preview_url: string | null;
  price_delta_cents: number;
  is_default: boolean;
  asset_overrides: { glb_url: string | null; usdz_url: string | null };
  // New — burner_type only, purely informational (never processed):
  reference_video_url: string | null;
  // New — burner_type only, the sole geometry-difference signal (no automated detection):
  has_distinct_geometry: boolean;
}

interface VariantGroup {
  id: string;
  label: string;
  type: "single_select";
  required: boolean;
  group_type: VariantGroupType;
  options: VariantOption[];
}

interface VariantsConfig {
  groups: VariantGroup[];
}

/* ------------------------------------------------------------------ */
/* Types                                                                */
/* ------------------------------------------------------------------ */

interface Manufacturer {
  id: string;
  name: string;
}

interface WizardState {
  wizardId: string;
  productId: string | null;

  // Product type (M5b). 'photogrammetry' is the shipped flow and the default;
  // 'parametric' forks step 4 and skips the media/variant/photo steps, which
  // have nothing to collect for a generated product.
  productType: ProductType;
  pergolaParams: PergolaParams;

  // Step 1
  consumerSheetFile: File | null;
  consumerSheetUrl: string | null;
  installSheetFile: File | null;
  installSheetUrl: string | null;
  consumerSpecs: ConsumerSpecs | null;
  installerSpecs: InstallerSpecs | null;
  clearances: Clearances | null;

  // Step 2
  name: string;
  category: string;
  manufacturerId: string;
  description: string;
  base_price: string;
  height: string;
  width: string;
  depth: string;
  weight: string;
  btu: string;
  fuelType: string;
  certifications: string;
  warranty: string;
  availableCountries: string[] | null;

  // Step 3
  heroFile: File | null;
  heroUrl: string | null;
  textureFiles: Record<TextureKey, File | null>;
  textureUrls: Record<TextureKey, string | null>;

  // Step 4 — GLB, 3D scan, and CAD are independent upload paths with no shared state
  glbFile: File | null;
  glbUrl: string | null;
  assetStatus: string | null;
  scanFile: File | null;
  cadFile: File | null;
  cadFileUrl: string | null;
  cadFileFormat: string | null;

  // Step 5 — Configuration
  variants: VariantsConfig;

  // Step 6
  photoUrls: string[];

  // Step 7
  publishActive: boolean;
  skippedSteps: number[];
}

function makeInitialState(): WizardState {
  return {
    wizardId: crypto.randomUUID(),
    productId: null,
    productType: "photogrammetry",
    pergolaParams: { ...DEFAULT_PERGOLA_PARAMS },
    consumerSheetFile: null,
    consumerSheetUrl: null,
    installSheetFile: null,
    installSheetUrl: null,
    consumerSpecs: null,
    installerSpecs: null,
    clearances: null,
    name: "",
    category: "",
    manufacturerId: "",
    description: "",
    base_price: "",
    height: "",
    width: "",
    depth: "",
    weight: "",
    btu: "",
    fuelType: "",
    certifications: "",
    warranty: "",
    availableCountries: null,
    heroFile: null,
    heroUrl: null,
    textureFiles: { diffuse: null, normal: null, roughness: null, metalness: null },
    textureUrls: { diffuse: null, normal: null, roughness: null, metalness: null },
    glbFile: null,
    glbUrl: null,
    assetStatus: null,
    scanFile: null,
    cadFile: null,
    cadFileUrl: null,
    cadFileFormat: null,
    variants: { groups: [] },
    photoUrls: Array(8).fill("") as string[],
    publishActive: true,
    skippedSteps: [],
  };
}

/* ------------------------------------------------------------------ */
/* Draft persistence helpers                                            */
/* ------------------------------------------------------------------ */

interface DraftPayload {
  step: number;
  state: WizardState;
  savedAt: string;
}

function formatDraftAge(savedAt: string): string {
  const ageMs = Date.now() - new Date(savedAt).getTime();
  const hours   = Math.floor(ageMs / 3_600_000);
  const minutes = Math.floor(ageMs / 60_000);
  if (hours   > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return "just now";
}

function DraftResumeBanner({
  savedAt,
  onResume,
  onDiscard,
}: {
  savedAt: string;
  onResume: () => void;
  onDiscard: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-xl border border-amber-800/60 bg-amber-900/20 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium text-amber-300">Unsaved draft found</p>
        <p className="text-xs text-amber-600 mt-0.5">
          You have an unsaved draft from {formatDraftAge(savedAt)}. Resume where you left off?
        </p>
      </div>
      <div className="flex shrink-0 gap-2">
        <button
          onClick={onResume}
          className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-700 transition"
        >
          Resume
        </button>
        <button
          onClick={onDiscard}
          className="rounded-lg border border-amber-700/50 px-3 py-1.5 text-xs text-amber-400 hover:bg-amber-900/40 transition"
        >
          Start fresh
        </button>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Network helpers                                                      */
/* ------------------------------------------------------------------ */

function xhrPut(
  url: string,
  file: File | Blob,
  contentType: string,
  onProgress: (pct: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!url) {
      reject(new Error(`xhrPut called with empty URL — presigned URL generation failed`));
      return;
    }
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) { onProgress(100); resolve(); }
      else reject(new Error(`Upload failed (${xhr.status}): ${xhr.responseText?.slice(0, 200)}`));
    };
    xhr.onerror = () => reject(new Error("Network error during upload"));
    xhr.send(file);
  });
}

async function apiPost<T>(path: string, body: unknown, jwt: string): Promise<T> {
  const res = await fetch(`${SB_URL}${path}`, {
    method: "POST",
    headers: {
      apikey: ANON_KEY,
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  if (!res.ok) throw new Error(`${path} failed (${res.status}): ${text.slice(0, 300)}`);
  return JSON.parse(text) as T;
}

async function apiPatch(
  table: string,
  id: string,
  fields: Record<string, unknown>,
  jwt: string
): Promise<void> {
  const res = await fetch(`${SB_URL}/rest/v1/${table}?id=eq.${id}`, {
    method: "PATCH",
    headers: {
      apikey: ANON_KEY,
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify(fields),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`PATCH ${table} failed (${res.status}): ${text.slice(0, 300)}`);
  }
}

async function getWizardUploadUrl(
  s3Key: string,
  contentType: string,
  jwt: string
): Promise<{ uploadUrl: string; publicUrl: string }> {
  const res = await apiPost<{ uploadUrl: string; url: string }>(
    "/functions/v1/get-wizard-upload-url",
    { s3_key: s3Key, content_type: contentType },
    jwt
  );
  if (!res.uploadUrl || !res.url) {
    throw new Error(`get-wizard-upload-url returned unexpected shape: ${JSON.stringify(res)}`);
  }
  return { uploadUrl: res.uploadUrl, publicUrl: res.url };
}

async function createProductDraft(
  state: WizardState,
  jwt: string
): Promise<string> {
  const res = await fetch(`${SB_URL}/functions/v1/create-product`, {
    method: "POST",
    headers: {
      apikey: ANON_KEY,
      Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      name: state.name.trim(),
      manufacturer_id: state.manufacturerId,
      category: state.category,
      asset_status: "pending",
      active: false,
    }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({})) as { error?: string };
    throw new Error(err.error || `Product create failed (${res.status})`);
  }
  const row = await res.json() as { id: string };
  return row.id;
}

async function checkAssetStatus(productId: string, jwt: string): Promise<string | null> {
  const res = await fetch(
    `${SB_URL}/rest/v1/products?id=eq.${productId}&select=asset_status`,
    { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
  );
  if (!res.ok) return null;
  const [row] = await res.json() as [{ asset_status: string | null }?];
  return row?.asset_status ?? null;
}

/* ------------------------------------------------------------------ */
/* Step Indicator                                                       */
/* ------------------------------------------------------------------ */

const STEP_LABELS = ["Spec Sheet", "Product Info", "Media", "3D Model", "Configuration", "Photos", "Review"];

/** `skipped` greys out steps that do not apply to the chosen product type,
 *  so the parametric path reads as 4 real steps rather than 7 with three
 *  mysteriously unreachable. Empty for photogrammetry. */
function StepIndicator({ current, skipped = [] }: { current: number; skipped?: number[] }) {
  return (
    <div className="flex items-center gap-0">
      {STEP_LABELS.map((label, i) => {
        const num = i + 1;
        const isSkipped = skipped.includes(num);
        const done = num < current && !isSkipped;
        const active = num === current;
        return (
          <div key={num} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={[
                  "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold border-2 transition-all",
                  isSkipped
                    ? "border-gray-800 bg-transparent text-gray-700"
                    : done
                    ? "border-orange-500 bg-orange-500 text-white"
                    : active
                    ? "border-orange-500 bg-transparent text-orange-400"
                    : "border-gray-700 bg-transparent text-gray-600",
                ].join(" ")}
                title={isSkipped ? "Not applicable to a parametric product" : undefined}
              >
                {isSkipped ? "–" : done ? "✓" : num}
              </div>
              <span
                className={[
                  "mt-1 text-xs whitespace-nowrap",
                  isSkipped ? "text-gray-700 line-through"
                    : active ? "text-orange-400 font-medium" : done ? "text-gray-400" : "text-gray-600",
                ].join(" ")}
              >
                {label}
              </span>
            </div>
            {i < STEP_LABELS.length - 1 && (
              <div
                className={[
                  "mx-2 mb-4 h-px w-8 sm:w-12",
                  done ? "bg-orange-500" : "bg-gray-800",
                ].join(" ")}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Shared UI primitives                                                 */
/* ------------------------------------------------------------------ */

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">
      {children}
    </label>
  );
}

const inputCls =
  "w-full rounded-lg border border-gray-700 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none";
const inputStyle = { background: "#111827", color: "#f9fafb" };

function NavButtons({
  onBack,
  onNext,
  onSkip,
  nextLabel = "Next →",
  skipLabel = "Skip →",
  nextDisabled = false,
  backLabel = "← Back",
  showBack = true,
}: {
  onBack?: () => void;
  onNext?: () => void;
  onSkip?: () => void;
  nextLabel?: string;
  skipLabel?: string;
  nextDisabled?: boolean;
  backLabel?: string;
  showBack?: boolean;
}) {
  return (
    <div className="flex justify-between pt-4 border-t border-gray-800">
      {showBack ? (
        <button
          onClick={onBack}
          className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 transition"
        >
          {backLabel}
        </button>
      ) : (
        <div />
      )}
      <div className="flex gap-3">
        {onSkip && (
          <button
            onClick={onSkip}
            className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 transition"
          >
            {skipLabel}
          </button>
        )}
        {onNext && (
          <button
            onClick={onNext}
            disabled={nextDisabled}
            className="rounded-lg bg-orange-600 px-5 py-2 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {nextLabel}
          </button>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 1 — Spec Sheets                                                 */
/* ------------------------------------------------------------------ */

function PdfDropZone({
  label,
  description,
  file,
  url,
  disabled,
  onFile,
}: {
  label: string;
  description: string;
  file: File | null;
  url: string | null;
  disabled: boolean;
  onFile: (f: File) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const uploaded = !!url;
  return (
    <div className="space-y-1.5">
      <div>
        <p className="text-sm font-medium text-gray-200">{label}</p>
        <p className="text-xs text-gray-500 mt-0.5">{description}</p>
      </div>
      <div
        onClick={() => !disabled && ref.current?.click()}
        className={[
          "flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-6 py-5 text-center cursor-pointer transition select-none",
          uploaded
            ? "border-green-600 bg-green-900/10"
            : file
            ? "border-orange-600/50 bg-orange-900/10"
            : "border-gray-700 bg-gray-900/60 hover:border-gray-500",
          disabled ? "pointer-events-none opacity-60" : "",
        ].join(" ")}
      >
        <input
          ref={ref}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          disabled={disabled}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
        />
        {uploaded ? (
          <>
            <span className="text-2xl">✅</span>
            <span className="text-xs font-medium text-green-400 break-all">{file?.name ?? "Uploaded"}</span>
            <span className="text-xs text-gray-600">Click to replace</span>
          </>
        ) : file ? (
          <>
            <span className="text-2xl">📄</span>
            <span className="text-xs font-medium text-orange-300 break-all">{file.name}</span>
            <span className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB — ready to upload</span>
          </>
        ) : (
          <>
            <span className="text-2xl text-gray-600">📄</span>
            <span className="text-xs text-gray-400">Drop PDF here or click to browse</span>
            <span className="text-xs text-gray-600">Optional</span>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Photogrammetry vs parametric, chosen before anything else because it decides
 * which later steps collect anything at all.
 *
 * Hidden entirely unless `parametric_products_enabled` is on — with the flag
 * off this renders nothing and the wizard is byte-for-byte the shipped flow.
 * The flag is re-checked server-side in generate-parametric-preview; a gate
 * that only works in the UI is not a gate (§6.5).
 *
 * Spec-sheet extraction runs on BOTH branches. It is arguably more valuable
 * here: a misread dimension on a parametric product yields a structurally
 * valid pergola of the wrong size, rather than an obviously wrong texture.
 */
function ProductTypeChooser({
  value, onChange,
}: { value: ProductType; onChange: (t: ProductType) => void }) {
  const { enabled, loading } = useFeatureFlag("parametric_products_enabled");
  if (loading || !enabled) return null;

  const options: { key: ProductType; title: string; blurb: string }[] = [
    {
      key: "photogrammetry",
      title: "Photogrammetry",
      blurb: "Built from product photos and uploaded source files. The standard path.",
    },
    {
      key: "parametric",
      title: "Parametric",
      blurb: "Generated from dimensions — no photos or mesh upload. Pergolas and shade structures.",
    },
  ];

  return (
    <div>
      <p className="mb-2 text-sm font-medium text-gray-300">Product type</p>
      <div className="grid gap-3 sm:grid-cols-2">
        {options.map((o) => {
          const selected = value === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => onChange(o.key)}
              aria-pressed={selected}
              className={[
                "rounded-xl border p-4 text-left transition",
                selected
                  ? "border-orange-500 bg-orange-500/10"
                  : "border-gray-800 bg-gray-950 hover:border-gray-700",
              ].join(" ")}
            >
              <span className={selected ? "font-semibold text-orange-300" : "font-semibold text-gray-200"}>
                {o.title}
              </span>
              <span className="mt-1 block text-xs text-gray-400">{o.blurb}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Step1({
  state,
  update,
  onNext,
  jwt,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
  onNext: () => void;
  jwt: string;
}) {
  const { toast } = useToast();
  const [phase, setPhase] = useState<"idle" | "uploading" | "extracting" | "done" | "error">("idle");
  const [uploadProgress, setUploadProgress] = useState<{ consumer: number; install: number }>({ consumer: 0, install: 0 });
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const busy = phase === "uploading" || phase === "extracting";
  const hasAnyFile = !!(state.consumerSheetFile || state.installSheetFile);
  const hasAnyUrl = !!(state.consumerSheetUrl || state.installSheetUrl);

  async function handleProcess() {
    if (!hasAnyFile && !hasAnyUrl) return;
    setErrMsg(null);
    setUploadProgress({ consumer: 0, install: 0 });

    try {
      setPhase("uploading");

      let consumerUrl = state.consumerSheetUrl;
      let installUrl = state.installSheetUrl;

      // Upload consumer sheet if a new file was selected
      if (state.consumerSheetFile && !state.consumerSheetUrl) {
        const key = `specs/${state.wizardId}/consumer.pdf`;
        const { uploadUrl, publicUrl } = await getWizardUploadUrl(key, "application/pdf", jwt);
        await xhrPut(uploadUrl, state.consumerSheetFile, "application/pdf", (pct) =>
          setUploadProgress((p) => ({ ...p, consumer: pct }))
        );
        consumerUrl = publicUrl;
        update({ consumerSheetUrl: publicUrl });
      }

      // Upload install sheet if a new file was selected
      if (state.installSheetFile && !state.installSheetUrl) {
        const key = `specs/${state.wizardId}/install.pdf`;
        const { uploadUrl, publicUrl } = await getWizardUploadUrl(key, "application/pdf", jwt);
        await xhrPut(uploadUrl, state.installSheetFile, "application/pdf", (pct) =>
          setUploadProgress((p) => ({ ...p, install: pct }))
        );
        installUrl = publicUrl;
        update({ installSheetUrl: publicUrl });
      }

      // Extract specs with Claude
      setPhase("extracting");
      const { consumerSpecs, installerSpecs, clearances, partial, extractionWarning } = await extractSpecSheet(
        state.name || "Product",
        state.category || "Other",
        jwt,
        consumerUrl ?? undefined,
        installUrl ?? undefined
      );

      // Pre-fill Step 2 fields
      update({
        consumerSheetUrl: consumerUrl,
        installSheetUrl: installUrl,
        consumerSpecs,
        installerSpecs,
        clearances: clearances ?? null,
        height: consumerSpecs.dimensions?.height ?? "",
        width: consumerSpecs.dimensions?.width ?? "",
        depth: consumerSpecs.dimensions?.depth ?? "",
        weight: consumerSpecs.weight ?? "",
        btu: consumerSpecs.btu_output ?? "",
        fuelType: consumerSpecs.fuel_type ?? "",
        certifications: (consumerSpecs.certifications ?? []).join(", "),
        warranty: consumerSpecs.warranty ?? "",
      });

      setPhase("done");
      if (partial) {
        toast({
          title: "Spec extraction completed with limited data",
          description: extractionWarning ?? "The spec sheet was too large to fully process — please review and fill in any missing fields.",
        });
      } else {
        const docCount = [consumerUrl, installUrl].filter(Boolean).length;
        toast({ title: "Specs extracted", description: `${docCount} document${docCount > 1 ? "s" : ""} processed — form pre-filled.` });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Unknown error";
      setErrMsg(msg);
      setPhase("error");
      toast({ title: "Extraction failed", description: msg });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Step 1 — Spec Sheets</h2>
        <p className="mt-1 text-sm text-gray-400">
          Upload one or both PDFs. Claude reads both and auto-fills the product form. Both are optional — you can fill manually.
        </p>
      </div>

      <ProductTypeChooser
        value={state.productType}
        onChange={(productType) => update({ productType })}
      />

      {/* Two drop zones */}
      <div className="space-y-4">
        <PdfDropZone
          label="Consumer Spec Sheet"
          description="Product overview, features, warranty, certifications — used for customer-facing display"
          file={state.consumerSheetFile}
          url={state.consumerSheetUrl}
          disabled={busy}
          onFile={(f) => { update({ consumerSheetFile: f, consumerSheetUrl: null }); setPhase("idle"); setErrMsg(null); }}
        />
        <PdfDropZone
          label="Technical / Installation Document"
          description="Dimensions, BTU, clearances, venting — used for installer specs and AI Install Check"
          file={state.installSheetFile}
          url={state.installSheetUrl}
          disabled={busy}
          onFile={(f) => { update({ installSheetFile: f, installSheetUrl: null }); setPhase("idle"); setErrMsg(null); }}
        />
      </div>

      {/* Upload progress */}
      {phase === "uploading" && (
        <div className="space-y-2">
          {state.consumerSheetFile && !state.consumerSheetUrl && (
            <div className="space-y-0.5">
              <div className="flex justify-between text-xs">
                <span className="text-orange-400">Uploading consumer sheet…</span>
                <span className="text-gray-500">{uploadProgress.consumer}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-gray-800 overflow-hidden">
                <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${uploadProgress.consumer}%` }} />
              </div>
            </div>
          )}
          {state.installSheetFile && !state.installSheetUrl && (
            <div className="space-y-0.5">
              <div className="flex justify-between text-xs">
                <span className="text-orange-400">Uploading install doc…</span>
                <span className="text-gray-500">{uploadProgress.install}%</span>
              </div>
              <div className="h-1.5 w-full rounded-full bg-gray-800 overflow-hidden">
                <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${uploadProgress.install}%` }} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Extracting */}
      {phase === "extracting" && (
        <div className="rounded-lg border border-blue-800 bg-blue-900/20 px-4 py-3 text-sm text-blue-300">
          ✦ Processing specification sheet…
        </div>
      )}

      {/* Success */}
      {phase === "done" && (
        <div className="rounded-lg border border-green-800 bg-green-900/20 px-4 py-3 text-sm text-green-400">
          ✓ Extraction complete — Product Info form pre-filled below.
        </div>
      )}

      {/* Error */}
      {phase === "error" && errMsg && (
        <p className="text-sm text-red-400 bg-red-900/20 border border-red-900/40 rounded-lg px-3 py-2">
          {errMsg}
        </p>
      )}

      {/* Extract button */}
      {hasAnyFile && phase !== "done" && (
        <button
          onClick={handleProcess}
          disabled={busy}
          className="w-full rounded-lg bg-orange-600 py-2.5 text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
        >
          {phase === "uploading"
            ? "Uploading…"
            : phase === "extracting"
            ? "Processing…"
            : "Extract with Claude"}
        </button>
      )}

      <NavButtons
        showBack={false}
        onNext={onNext}
        nextLabel={phase === "done" ? "Continue with pre-filled data →" : "Skip — I'll fill manually →"}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 2 — Product Info                                                */
/* ------------------------------------------------------------------ */

function Step2({
  state,
  update,
  onBack,
  onNext,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const [manufacturers, setManufacturers] = useState<Manufacturer[]>([]);

  useEffect(() => {
    fetch(`${SB_URL}/rest/v1/manufacturers?select=id,name&order=name`, {
      headers: { apikey: ANON_KEY },
    })
      .then((r) => r.json())
      .then((data) => setManufacturers(data ?? []))
      .catch(() => {});
  }, []);

  const canContinue = state.name.trim() && state.category && state.manufacturerId;

  const set = (k: keyof WizardState) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    update({ [k]: e.target.value });

  // Shown only after Claude extraction has run at least once
  const extractedOnce = !!state.consumerSpecs;

  // Inline confidence dot — green: value looks solid, amber: weak/short, red: empty
  const ConfidenceDot = ({ value }: { value: unknown }) => {
    if (!extractedOnce) return null;
    const str = String(value ?? "").trim();
    const isEmpty = !str || str === "null";
    const isWeak =
      str.length <= 3 || ["n/a", "tbd", "-", "unknown"].includes(str.toLowerCase());
    const color = isEmpty ? "#EF4444" : isWeak ? "#F59E0B" : "#10B981";
    return (
      <span
        title={
          isEmpty
            ? "Not found in spec sheet"
            : isWeak
            ? "Low confidence extraction"
            : "Extracted from spec sheet"
        }
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          backgroundColor: color,
          marginLeft: 6,
          verticalAlign: "middle",
        }}
      />
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Step 2 — Product Info</h2>
        <p className="mt-1 text-sm text-gray-400">
          {state.consumerSpecs ? "Fields pre-filled from spec sheet — review and correct as needed." : "Fill in the product details."}
        </p>
      </div>

      {/* Required fields */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <FieldLabel>Product Name * <ConfidenceDot value={state.name} /></FieldLabel>
          <input type="text" value={state.name} onChange={set("name")} placeholder="e.g. Oslo Direct Vent Gas Fireplace" style={inputStyle} className={inputCls} />
        </div>

        <div>
          <FieldLabel>Category * <ConfidenceDot value={state.category} /></FieldLabel>
          <select value={state.category} onChange={set("category")} style={inputStyle} className={inputCls}>
            <option value="">Select category…</option>
            {CATEGORY_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <div>
          <FieldLabel>Manufacturer *</FieldLabel>
          <select value={state.manufacturerId} onChange={set("manufacturerId")} style={inputStyle} className={inputCls}>
            <option value="">Select manufacturer…</option>
            {manufacturers.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>

        <div>
          <FieldLabel>MSRP (USD) <ConfidenceDot value={state.base_price} /></FieldLabel>
          <input type="number" value={state.base_price} onChange={set("base_price")} placeholder="0.00" style={inputStyle} className={inputCls} />
        </div>

        <div>
          <FieldLabel>Fuel Type <ConfidenceDot value={state.fuelType} /></FieldLabel>
          <input type="text" value={state.fuelType} onChange={set("fuelType")} placeholder="e.g. Natural Gas / Propane" style={inputStyle} className={inputCls} />
        </div>
      </div>

      {/* Description */}
      <div>
        <FieldLabel>Description <ConfidenceDot value={state.description} /></FieldLabel>
        <textarea
          value={state.description}
          onChange={(e) => update({ description: e.target.value })}
          placeholder="Product description for retailers and end-users…"
          rows={3}
          style={inputStyle}
          className={inputCls + " resize-none"}
        />
      </div>

      {/* Dimensions & specs */}
      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Dimensions & Specs</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          <div>
            <FieldLabel>Height <ConfidenceDot value={state.height} /></FieldLabel>
            <input type="text" value={state.height} onChange={set("height")} placeholder='e.g. 42"' style={inputStyle} className={inputCls} />
          </div>
          <div>
            <FieldLabel>Width <ConfidenceDot value={state.width} /></FieldLabel>
            <input type="text" value={state.width} onChange={set("width")} placeholder='e.g. 36"' style={inputStyle} className={inputCls} />
          </div>
          <div>
            <FieldLabel>Depth <ConfidenceDot value={state.depth} /></FieldLabel>
            <input type="text" value={state.depth} onChange={set("depth")} placeholder='e.g. 18"' style={inputStyle} className={inputCls} />
          </div>
          <div>
            <FieldLabel>Weight</FieldLabel>
            <input type="text" value={state.weight} onChange={set("weight")} placeholder="e.g. 285 lbs" style={inputStyle} className={inputCls} />
          </div>
          <div>
            <FieldLabel>BTU Output <ConfidenceDot value={state.btu} /></FieldLabel>
            <input type="text" value={state.btu} onChange={set("btu")} placeholder="e.g. 30,000 BTU" style={inputStyle} className={inputCls} />
          </div>
          <div>
            <FieldLabel>Warranty</FieldLabel>
            <input type="text" value={state.warranty} onChange={set("warranty")} placeholder="e.g. 5 years" style={inputStyle} className={inputCls} />
          </div>
        </div>
      </div>

      <div>
        <FieldLabel>Certifications <ConfidenceDot value={state.certifications} /></FieldLabel>
        <input type="text" value={state.certifications} onChange={set("certifications")} placeholder="e.g. CSA, UL, EPA (comma-separated)" style={inputStyle} className={inputCls} />
        <p className="mt-1 text-xs text-gray-600">Separate multiple certifications with commas</p>
      </div>

      <div>
        <FieldLabel>Available Countries</FieldLabel>
        <div className="flex flex-wrap gap-2">
          {(
            [
              { code: null,  label: "All Countries" },
              { code: "CA",  label: "🇨🇦 Canada" },
              { code: "US",  label: "🇺🇸 United States" },
            ] as { code: string | null; label: string }[]
          ).map(({ code, label }) => {
            const isAll = code === null;
            const active = isAll
              ? state.availableCountries === null
              : (state.availableCountries ?? []).includes(code!);
            return (
              <button
                key={String(code)}
                type="button"
                onClick={() => {
                  if (isAll) {
                    update({ availableCountries: null });
                  } else {
                    const current = state.availableCountries ?? [];
                    const next = current.includes(code!)
                      ? current.filter((c) => c !== code!)
                      : [...current, code!];
                    update({ availableCountries: next.length === 0 ? null : next });
                  }
                }}
                className={[
                  "rounded-lg border px-3 py-1.5 text-sm transition",
                  active
                    ? "border-teal-600 bg-teal-900/40 text-teal-300"
                    : "border-gray-700 bg-transparent text-gray-400 hover:border-gray-500",
                ].join(" ")}
              >
                {label}
              </button>
            );
          })}
        </div>
        <p className="mt-1 text-xs text-gray-600">
          "All Countries" (default) means no restriction. Select specific markets to limit visibility.
        </p>
      </div>

      <NavButtons onBack={onBack} onNext={onNext} nextDisabled={!canContinue} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 3 — Media Assets                                                */
/* ------------------------------------------------------------------ */

function FileUploadSlot({
  label,
  hint,
  accept,
  file,
  url,
  tip,
  disabled,
  onFile,
}: {
  label: string;
  hint: string;
  accept: string;
  file: File | null;
  url: string | null;
  tip?: string;
  disabled: boolean;
  onFile: (f: File) => void;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const isImage = accept.includes("image");

  return (
    <div>
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{label}</span>
        {tip && (
          <span title={tip} className="cursor-help text-gray-600 hover:text-gray-400 text-xs">ⓘ</span>
        )}
      </div>
      <div
        onClick={() => !disabled && ref.current?.click()}
        className={[
          "flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed p-4 text-center cursor-pointer transition select-none min-h-[88px]",
          url ? "border-green-600 bg-green-900/10" : "border-gray-700 bg-gray-900/60 hover:border-gray-500",
          disabled ? "pointer-events-none opacity-50" : "",
        ].join(" ")}
      >
        <input ref={ref} type="file" accept={accept} className="hidden" disabled={disabled}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
        {url ? (
          <>
            {isImage && <img src={url} alt={label} className="h-12 w-12 object-cover rounded" />}
            <span className="text-xs text-green-400 break-all">{file?.name ?? "Uploaded"}</span>
          </>
        ) : (
          <>
            <span className="text-lg text-gray-600">{isImage ? "🖼️" : "📁"}</span>
            <span className="text-xs text-gray-500">{hint}</span>
          </>
        )}
      </div>
    </div>
  );
}

function Step3({
  state,
  update,
  onBack,
  onNext,
  onSkip,
  jwt,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  jwt: string;
}) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState<string | null>(null);

  async function uploadMedia(file: File, slotKey: string, contentType: string) {
    setUploading(slotKey);
    try {
      const basePath = state.productId
        ? `products/${state.productId}/${slotKey}`
        : `wizard/${state.wizardId}/${slotKey}`;
      const s3Key = `${basePath}.${file.name.split(".").pop()}`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, contentType, jwt);
      await xhrPut(uploadUrl, file, contentType, () => {});
      return publicUrl;
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      toast({ title: "Upload failed", description: msg });
      return null;
    } finally {
      setUploading(null);
    }
  }

  async function onHeroFile(file: File) {
    update({ heroFile: file, heroUrl: null });
    const url = await uploadMedia(file, "hero", file.type || "image/jpeg");
    if (url) update({ heroUrl: url });
  }

  async function onTextureFile(key: TextureKey, file: File) {
    update({ textureFiles: { ...state.textureFiles, [key]: file }, textureUrls: { ...state.textureUrls, [key]: null } });
    const url = await uploadMedia(file, `textures/${key}`, file.type || "image/png");
    if (url) update({ textureUrls: { ...state.textureUrls, [key]: url } });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Step 3 — Media Assets</h2>
        <p className="mt-1 text-sm text-gray-400">
          Upload product images and texture maps. Files upload immediately on selection.
          {uploading && <span className="text-orange-400"> Uploading {uploading}…</span>}
        </p>
      </div>

      {/* Hero image */}
      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Hero Image</p>
        <FileUploadSlot
          label="Hero Image"
          hint="Main product photo (JPG/PNG)"
          accept="image/jpeg,image/png,image/webp"
          file={state.heroFile}
          url={state.heroUrl}
          disabled={!!uploading}
          onFile={onHeroFile}
        />
      </div>

      {/* Texture maps */}
      <div>
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-3">Texture Maps</p>
        <div className="grid grid-cols-2 gap-3">
          {TEXTURE_MAPS.map(({ key, label, tip }) => (
            <FileUploadSlot
              key={key}
              label={label}
              hint="PNG or JPG"
              accept="image/png,image/jpeg,image/webp"
              file={state.textureFiles[key]}
              url={state.textureUrls[key]}
              tip={tip}
              disabled={!!uploading}
              onFile={(f) => onTextureFile(key, f)}
            />
          ))}
        </div>
      </div>

      <NavButtons
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        skipLabel="Skip — add images later →"
        nextLabel="Next →"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 4 — 3D Model                                                    */
/* ------------------------------------------------------------------ */

const ASSET_STATUS_META: Record<string, { label: string; classes: string }> = {
  pending:    { label: "Pending",    classes: "bg-yellow-900/40 text-yellow-400 border-yellow-800" },
  processing: { label: "Processing", classes: "bg-blue-900/40 text-blue-400 border-blue-800" },
  complete:   { label: "Complete",   classes: "bg-green-900/40 text-green-400 border-green-800" },
  failed:     { label: "Failed",     classes: "bg-red-900/40 text-red-400 border-red-800" },
};

function AssetStatusBadge({ status }: { status: string | null }) {
  const meta = ASSET_STATUS_META[status ?? ""] ?? { label: status ?? "Unknown", classes: "bg-gray-800 text-gray-400 border-gray-700" };
  return (
    <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${meta.classes}`}>
      {meta.label}
    </span>
  );
}

/**
 * Step 4, parametric branch — the parameter form that replaces the GLB/CAD
 * upload. These numbers become geometry directly: buildFixedSlatPergola turns
 * them into posts, beams, rafters and slats, and M5 bakes a shadow atlas from
 * the same mesh.
 *
 * ⚠️ No browser preview render here, deliberately (Non-negotiable 3). A
 * TypeScript reimplementation of the generator would drift from the Lambda, and
 * a preview that disagrees with the shipped asset is worse than a slow one.
 * Preview comes from generate-parametric-preview, which runs the real
 * generator, on publish.
 */
function Step4Parametric({
  state, update, onBack, onNext,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  const params = state.pergolaParams;
  const errors = validatePergolaParams(params);
  const hasErrors = Object.keys(errors).length > 0;

  const setParam = (key: keyof PergolaParams, raw: string) =>
    update({ pergolaParams: { ...params, [key]: raw === "" ? NaN : Number(raw) } });

  const groups = ["Span", "Sections", "Slats"] as const;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Step 4 — Parameters</h2>
        <p className="mt-1 text-sm text-gray-400">
          Dimensions for a fixed-slat pergola. There is no file to upload — the mesh,
          finishes and shadow atlas are generated from these values. Defaults are the
          verified 12&times;16 reference structure.
        </p>
      </div>

      {groups.map((group) => (
        <div key={group}>
          <p className="mb-2 text-sm font-medium text-gray-300">{group}</p>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {PERGOLA_PARAM_FIELDS.filter((f) => f.group === group).map((f) => {
              const err = errors[f.key];
              const value = params[f.key];
              return (
                <label key={f.key} className="flex flex-col gap-1">
                  <span className="text-sm text-gray-400">
                    {f.label}{f.unit && <span className="text-gray-600"> ({f.unit})</span>}
                  </span>
                  <input
                    type="number"
                    step={f.step ?? 1}
                    value={Number.isFinite(value) ? String(value) : ""}
                    onChange={(e) => setParam(f.key, e.target.value)}
                    className={[
                      "rounded-lg border bg-gray-950 px-3 py-2 text-sm",
                      err ? "border-red-700" : "border-gray-800",
                    ].join(" ")}
                  />
                  {err
                    ? <span className="text-xs text-red-400">{err}</span>
                    : f.hint && <span className="text-xs text-gray-600">{f.hint}</span>}
                </label>
              );
            })}
          </div>
        </div>
      ))}

      <div className="flex justify-between pt-2">
        <button onClick={onBack} className="rounded-lg border border-gray-700 px-4 py-2 text-sm">
          Back
        </button>
        <button
          onClick={onNext}
          disabled={hasErrors}
          title={hasErrors ? "Fix the highlighted parameters first" : undefined}
          className="rounded-lg bg-orange-500 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          Continue
        </button>
      </div>
    </div>
  );
}

function Step4({
  state,
  update,
  onBack,
  onNext,
  onSkip,
  jwt,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  jwt: string;
}) {
  const { toast } = useToast();
  const fileRef     = useRef<HTMLInputElement>(null);
  const scanFileRef = useRef<HTMLInputElement>(null);
  const cadFileRef  = useRef<HTMLInputElement>(null);
  const [uploadPhase,     setUploadPhase]     = useState<"idle" | "uploading" | "done" | "error">("idle");
  const [uploadPct,       setUploadPct]       = useState(0);
  const [scanUploadPhase, setScanUploadPhase] = useState<"idle" | "uploading" | "done" | "error">("idle");
  const [scanUploadPct,   setScanUploadPct]   = useState(0);
  const [cadUploadPhase,  setCadUploadPhase]  = useState<"idle" | "uploading" | "done" | "error">("idle");
  const [cadUploadPct,    setCadUploadPct]    = useState(0);

  function detectCadFormat(filename: string): string {
    const ext = filename.split(".").pop()?.toLowerCase() ?? "";
    const map: Record<string, string> = { step: "STEP", stp: "STEP", obj: "OBJ", fbx: "FBX", dxf: "DXF" };
    return map[ext] ?? "OTHER";
  }

  // GLB files start with the 4-byte ASCII magic "glTF" (glTF spec, chunk 0 header).
  // Catches wrong-file uploads (e.g. a DXF spec drawing selected by mistake) before
  // they land at raw/{id}/object.glb and silently break the glb-assembly pipeline.
  async function validateGlbFile(file: File): Promise<string | null> {
    if (!file.name.toLowerCase().endsWith(".glb")) {
      return `Expected a .glb file, but got "${file.name}". Select the exported 3D model file, not a CAD drawing or spec sheet.`;
    }
    const headerBytes = new Uint8Array(await file.slice(0, 4).arrayBuffer());
    const magic = String.fromCharCode(...headerBytes);
    if (magic !== "glTF") {
      const shown = magic.replace(/[^\x20-\x7e]/g, "?") || "(empty)";
      return `This file doesn't look like a valid GLB (expected "glTF" header, found "${shown}"). ` +
        `Double-check you selected the 3D model export, not another file type.`;
    }
    return null;
  }

  // Poll asset status while productId exists and not complete/failed
  useEffect(() => {
    if (!state.productId) return;
    if (isAssetPipelineFinished(state.assetStatus) || state.assetStatus === "failed") return;

    const id = setInterval(async () => {
      const status = await checkAssetStatus(state.productId!, jwt);
      if (status && status !== state.assetStatus) {
        update({ assetStatus: status });
        if (isAssetPipelineFinished(status)) toast({ title: "3D model ready", description: "GLB and USDZ processed." });
      }
    }, 8000);
    return () => clearInterval(id);
  }, [state.productId, state.assetStatus]);

  async function handleGlbUpload(file: File) {
    if (!state.productId) {
      toast({ title: "Product not created yet", description: "Complete Step 2 first." });
      return;
    }

    const validationError = await validateGlbFile(file);
    if (validationError) {
      toast({ title: "Invalid GLB file", description: validationError });
      return;
    }

    update({ glbFile: file });
    setUploadPct(0);
    setUploadPhase("uploading");

    try {
      // Upload directly to the key the glb-assembly Lambda expects
      const s3Key = `raw/${state.productId}/object.glb`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, "model/gltf-binary", jwt);
      await xhrPut(uploadUrl, file, "model/gltf-binary", setUploadPct);

      await apiPatch("products", state.productId, { glb_url: publicUrl, asset_status: "pending" }, jwt);
      update({ glbUrl: publicUrl, assetStatus: "pending" });

      // Fire PBR → GLB assembly chain (non-blocking)
      void supabase.functions.invoke("trigger-pbr-pipeline", {
        body: { product_id: state.productId },
        headers: { Authorization: `Bearer ${jwt}` },
      }).then(({ error }) => {
        if (error) console.error("[handleGlbUpload] trigger-pbr-pipeline:", error);
        else console.log("[handleGlbUpload] trigger-pbr-pipeline fired for", state.productId);
      });

      setUploadPhase("done");
      toast({ title: "AR model uploaded", description: "PBR pipeline started — processing in background." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadPhase("error");
      toast({ title: "Upload failed", description: msg });
    }
  }

  // Only one scan tool is wired up today (Kiri Engine) — a named constant
  // rather than a hardcoded literal in the flag string below, so adding a
  // second tool later is a one-line change here plus a second button, not
  // a rewrite. Not a tool-selector UI: nothing else exists to select yet,
  // and building one now would be designing for a requirement nobody has.
  const SCAN_SOURCE_LABEL = "kiri_engine";

  // Scan-import GLB upload (Kiri Engine Pro / LiDAR scans) — deliberately a
  // separate function from handleGlbUpload() rather than a shared one with
  // a mode flag, even though the upload mechanics are identical (same
  // validateGlbFile() reused verbatim, same raw/{id}/object.glb key, same
  // trigger-pbr-pipeline chain — see handleGlbUpload() above). The one
  // real difference is the PATCH: this is the first place in the wizard
  // that ever writes geometry_source, and it needs its own distinct
  // upload state/UI so an admin uploading a Kiri export doesn't reach for
  // the generic "AR Model" button instead, which would silently leave
  // geometry_source null and route it through process-glb-assembly's
  // generic "unknown source" default rather than the scan_import probe
  // added for this exact case.
  async function handleScanImportUpload(file: File) {
    if (!state.productId) {
      toast({ title: "Product not created yet", description: "Complete Step 2 first." });
      return;
    }

    const validationError = await validateGlbFile(file);
    if (validationError) {
      toast({ title: "Invalid GLB file", description: validationError });
      return;
    }

    update({ scanFile: file });
    setScanUploadPct(0);
    setScanUploadPhase("uploading");

    try {
      // Same key handleGlbUpload() uses — confirmed this is what
      // process-glb-assembly's has_pbr/raw-GLB routing expects for both
      // manual uploads and scan_import (see probe_scan_import_orientation()/
      // probe_manual_glb_upload_orientation()).
      const s3Key = `raw/${state.productId}/object.glb`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, "model/gltf-binary", jwt);
      await xhrPut(uploadUrl, file, "model/gltf-binary", setScanUploadPct);

      await apiPatch("products", state.productId, {
        glb_url:         publicUrl,
        asset_status:    "pending",
        geometry_source: "scan_import",
        geometry_flags:  [`scan_source:${SCAN_SOURCE_LABEL}`],
      }, jwt);
      update({ glbUrl: publicUrl, assetStatus: "pending" });

      // Same trigger as handleGlbUpload() — process-pbr-materials already
      // auto-invokes process-glb-assembly at the end of its own run
      // regardless of geometry_source, so no separate/additional trigger
      // is needed for this to reach the scan_import routing added there.
      void supabase.functions.invoke("trigger-pbr-pipeline", {
        body: { product_id: state.productId },
        headers: { Authorization: `Bearer ${jwt}` },
      }).then(({ error }) => {
        if (error) console.error("[handleScanImportUpload] trigger-pbr-pipeline:", error);
        else console.log("[handleScanImportUpload] trigger-pbr-pipeline fired for", state.productId);
      });

      setScanUploadPhase("done");
      toast({ title: "3D scan uploaded", description: "PBR pipeline started — processing in background." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setScanUploadPhase("error");
      toast({ title: "Upload failed", description: msg });
    }
  }

  async function handleCadUpload(file: File) {
    if (!state.productId) {
      toast({ title: "Product not created yet", description: "Complete Step 2 first." });
      return;
    }
    update({ cadFile: file });
    setCadUploadPct(0);
    setCadUploadPhase("uploading");
    try {
      const s3Key = `raw-assets/${state.productId}/cad/${file.name}`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, "application/octet-stream", jwt);
      await xhrPut(uploadUrl, file, "application/octet-stream", setCadUploadPct);
      const format = detectCadFormat(file.name);

      console.log("[handleCadUpload] productId:", state.productId, "— PATCHing cad_file_url:", publicUrl, "format:", format);
      await apiPatch("products", state.productId, { cad_file_url: publicUrl, cad_file_format: format }, jwt);
      console.log("[handleCadUpload] PATCH succeeded — cad_file_url written to DB");

      update({ cadFileUrl: publicUrl, cadFileFormat: format });

      // Fire geometry extraction independently — does NOT trigger PBR pipeline
      void supabase.functions.invoke("trigger-geometry-pipeline", {
        body: { product_id: state.productId },
        headers: { Authorization: `Bearer ${jwt}` },
      }).then(({ error }) => {
        if (error) console.error("[handleCadUpload] trigger-geometry-pipeline:", error);
        else console.log("[handleCadUpload] trigger-geometry-pipeline fired for", state.productId);
      });

      setCadUploadPhase("done");
      toast({ title: "CAD file uploaded", description: `Format: ${format} — geometry extraction started.` });
    } catch (err) {
      console.error("[handleCadUpload] error:", err);
      setCadUploadPhase("error");
      toast({ title: "CAD upload failed", description: err instanceof Error ? err.message : "Upload failed" });
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Step 4 — 3D Model</h2>
        <p className="mt-1 text-sm text-gray-400">
          Upload a GLB file to trigger the AR processing pipeline. This step is optional — you can publish and add the 3D model later.
        </p>
      </div>

      {/* Pipeline status */}
      {state.productId && (
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Pipeline Status</span>
            <AssetStatusBadge status={state.assetStatus} />
          </div>
          {state.assetStatus === "processing" && (
            <p className="text-xs text-blue-400">Processing… this typically takes 2–5 minutes.</p>
          )}
          {isAssetPipelineFinished(state.assetStatus) && (
            <p className="text-xs text-green-400">GLB and USDZ ready for AR placement.</p>
          )}
          {state.assetStatus === "failed" && (
            <p className="text-xs text-red-400">Pipeline failed — try re-uploading the GLB file.</p>
          )}
          {(state.assetStatus === "processing" || state.assetStatus === "pending") && (
            <p className="text-xs text-gray-600">Auto-refreshing every 8 seconds…</p>
          )}
        </div>
      )}

      {/* GLB drop zone */}
      <div>
        <p className="text-sm font-medium text-gray-300 mb-1">3D Model (GLB)</p>
        <p className="text-xs text-gray-500 mb-2">Upload an already-textured .glb file for immediate AR preview</p>
        <div
          onClick={() => uploadPhase !== "uploading" && fileRef.current?.click()}
          className={[
            "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition select-none",
            uploadPhase === "done" ? "border-green-600 bg-green-900/10" : "border-gray-700 bg-gray-900/60 hover:border-gray-500",
            uploadPhase === "uploading" ? "pointer-events-none opacity-60" : "",
          ].join(" ")}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".glb"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleGlbUpload(f); }}
          />
          {state.glbFile ? (
            <>
              <span className="text-3xl">📦</span>
              <span className="text-sm font-medium text-green-400 break-all">{state.glbFile.name}</span>
              <span className="text-xs text-gray-500">
                {(state.glbFile.size / 1024 / 1024).toFixed(2)} MB — click to replace
              </span>
            </>
          ) : (
            <>
              <span className="text-3xl text-gray-600">📦</span>
              <span className="text-sm text-gray-400">Drop .glb file here</span>
              <span className="text-xs text-gray-600">or click to browse</span>
            </>
          )}
        </div>
      </div>

      {/* GLB upload progress */}
      {uploadPhase === "uploading" && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-orange-400">Uploading 3D model…</span>
            <span className="text-gray-500">{uploadPct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-gray-800 overflow-hidden">
            <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${uploadPct}%` }} />
          </div>
        </div>
      )}

      {/* 3D scan (Kiri Engine / LiDAR) GLB drop zone — distinct from the
          plain GLB uploader above: this one sets geometry_source so
          process-glb-assembly routes it through the scan_import probe
          instead of the generic "unknown source" default. */}
      <div>
        <p className="text-sm font-medium text-gray-300 mb-1">3D Scan (GLB)</p>
        <p className="text-xs text-gray-500 mb-2">
          Upload a Kiri Engine Pro or LiDAR scan export (.glb) — for scanned products, use this instead of "3D Model" above
        </p>
        <div
          onClick={() => scanUploadPhase !== "uploading" && scanFileRef.current?.click()}
          className={[
            "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-8 text-center cursor-pointer transition select-none",
            scanUploadPhase === "done" ? "border-green-600 bg-green-900/10" : "border-gray-700 bg-gray-900/60 hover:border-gray-500",
            scanUploadPhase === "uploading" ? "pointer-events-none opacity-60" : "",
          ].join(" ")}
        >
          <input
            ref={scanFileRef}
            type="file"
            accept=".glb"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleScanImportUpload(f); }}
          />
          {state.scanFile ? (
            <>
              <span className="text-3xl">📷</span>
              <span className="text-sm font-medium text-green-400 break-all">{state.scanFile.name}</span>
              <span className="text-xs text-gray-500">
                {(state.scanFile.size / 1024 / 1024).toFixed(2)} MB — click to replace
              </span>
            </>
          ) : (
            <>
              <span className="text-3xl text-gray-600">📷</span>
              <span className="text-sm text-gray-400">Drop scan .glb file here</span>
              <span className="text-xs text-gray-600">or click to browse</span>
            </>
          )}
        </div>
      </div>

      {/* 3D scan upload progress */}
      {scanUploadPhase === "uploading" && (
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-orange-400">Uploading 3D scan…</span>
            <span className="text-gray-500">{scanUploadPct}%</span>
          </div>
          <div className="h-1.5 w-full rounded-full bg-gray-800 overflow-hidden">
            <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${scanUploadPct}%` }} />
          </div>
        </div>
      )}
      {scanUploadPhase === "error" && (
        <p className="text-xs text-red-400">Upload failed — try again.</p>
      )}

      {/* CAD file drop zone */}
      <div>
        <p className="text-sm font-medium text-gray-300 mb-1">CAD File (Optional)</p>
        <p className="text-xs text-gray-500 mb-2">Upload CAD source for geometry extraction (.step .obj .fbx .dxf)</p>
        <div
          onClick={() => cadUploadPhase !== "uploading" && cadFileRef.current?.click()}
          className={[
            "flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 text-center cursor-pointer transition select-none",
            cadUploadPhase === "done" ? "border-green-600 bg-green-900/10" : "border-gray-700 bg-gray-900/60 hover:border-gray-500",
            cadUploadPhase === "uploading" ? "pointer-events-none opacity-60" : "",
          ].join(" ")}
        >
          <input
            ref={cadFileRef}
            type="file"
            accept=".step,.stp,.obj,.fbx,.dxf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) handleCadUpload(f); }}
          />
          {state.cadFile ? (
            <>
              <span className="text-2xl">📐</span>
              <span className="text-sm font-medium text-green-400 break-all">{state.cadFile.name}</span>
              <span className="text-xs text-gray-500">
                {(state.cadFile.size / 1024 / 1024).toFixed(2)} MB · {state.cadFileFormat ?? "—"} — click to replace
              </span>
            </>
          ) : (
            <>
              <span className="text-2xl text-gray-600">📐</span>
              <span className="text-sm text-gray-400">Drop CAD file here</span>
              <span className="text-xs text-gray-600">or click to browse (.step, .stp, .obj, .fbx, .dxf)</span>
            </>
          )}
        </div>
        {cadUploadPhase === "uploading" && (
          <div className="mt-2 space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-orange-400">Uploading CAD file…</span>
              <span className="text-gray-500">{cadUploadPct}%</span>
            </div>
            <div className="h-1.5 w-full rounded-full bg-gray-800 overflow-hidden">
              <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${cadUploadPct}%` }} />
            </div>
          </div>
        )}
        {cadUploadPhase === "error" && (
          <p className="mt-1 text-xs text-red-400">Upload failed — try again.</p>
        )}
      </div>

      {!state.productId && (
        <p className="text-xs text-yellow-600">
          Product record will be created when you continue from Step 2. Return here after completing Steps 2–3.
        </p>
      )}

      <NavButtons
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        skipLabel="Skip — add 3D model later →"
        nextLabel="Review & Publish →"
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 5 — Configuration (Variants)                                   */
/* ------------------------------------------------------------------ */

function MobilePreview({
  productName,
  heroUrl,
  groups,
  selected,
  onSelect,
  basePrice,
}: {
  productName: string;
  heroUrl: string | null;
  groups: VariantGroup[];
  selected: Record<string, string>;
  onSelect: (groupId: string, optionId: string) => void;
  basePrice: string;
}) {
  let priceDelta = 0;
  for (const g of groups) {
    const opt = g.options.find((o) => o.id === selected[g.id]);
    if (opt) priceDelta += opt.price_delta_cents;
  }
  const base = parseFloat(basePrice) || 0;
  const total = base + priceDelta / 100;

  return (
    <div className="flex flex-col rounded-[24px] border border-gray-700 bg-[#0d0d0f] overflow-hidden w-[210px] flex-shrink-0 shadow-2xl">
      <div className="flex items-center justify-between px-4 py-1.5 bg-black">
        <span className="text-white text-[10px]">9:41</span>
        <span className="text-white text-[10px]">●●●</span>
      </div>
      <div className="h-32 bg-gray-900 flex items-center justify-center overflow-hidden">
        {heroUrl ? (
          <img src={heroUrl} alt={productName} className="w-full h-full object-cover" />
        ) : (
          <span className="text-4xl text-gray-700">📦</span>
        )}
      </div>
      <div className="bg-[#1c1c1e] flex-1 px-3 pt-3 pb-4 space-y-3">
        <div>
          <p className="text-white text-xs font-semibold line-clamp-1">{productName || "Product Name"}</p>
          {total > 0 && (
            <p className="text-[11px] font-medium mt-0.5" style={{ color: "#FF6B35" }}>
              ${total.toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}
              {priceDelta !== 0 && (
                <span className="text-gray-500 text-[10px] ml-1">
                  ({priceDelta > 0 ? "+" : ""}${(priceDelta / 100).toFixed(2)})
                </span>
              )}
            </p>
          )}
        </div>

        {groups.map((g) => {
          const selId = selected[g.id];
          const selOpt = g.options.find((o) => o.id === selId);
          return (
            <div key={g.id} className="space-y-1.5">
              <p className="text-gray-500 text-[10px] uppercase tracking-wider">{g.label || "Variant"}</p>
              <div className="flex flex-wrap gap-1.5">
                {g.options.map((opt) => {
                  const active = selId === opt.id;
                  return opt.swatch_url ? (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onSelect(g.id, opt.id)}
                      className={`w-6 h-6 rounded-full overflow-hidden border-2 transition ${
                        active ? "border-orange-500 scale-110" : "border-transparent"
                      }`}
                    >
                      <img src={opt.swatch_url} alt={opt.label} className="w-full h-full object-cover" />
                    </button>
                  ) : (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onSelect(g.id, opt.id)}
                      className={`rounded-full px-2 py-0.5 text-[10px] border transition ${
                        active ? "border-orange-500 text-orange-400 bg-orange-900/20" : "border-gray-700 text-gray-500 hover:border-gray-500"
                      }`}
                    >
                      {opt.label || "?"}
                    </button>
                  );
                })}
              </div>
              {selOpt?.label && (
                <p className="text-white text-[10px]">{selOpt.label}</p>
              )}
            </div>
          );
        })}

        {groups.length === 0 && (
          <p className="text-gray-700 text-[10px] text-center py-3">Add groups to preview</p>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function VariantOptionRow({
  option,
  groupId,
  groupType,
  isOnly,
  onUpdate,
  onRemove,
  onSetDefault,
  onUploadSwatch,
  onUploadPreview,
  uploadingKey,
}: {
  option: VariantOption;
  groupId: string;
  groupType: VariantGroupType;
  isOnly: boolean;
  onUpdate: (fields: Partial<VariantOption>) => void;
  onRemove: () => void;
  onSetDefault: () => void;
  onUploadSwatch: (file: File) => void;
  onUploadPreview: (file: File) => void;
  uploadingKey: string | null;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const swatchRef = useRef<HTMLInputElement>(null);
  const previewRef = useRef<HTMLInputElement>(null);
  const swatchKey = `${groupId}/${option.id}/swatch`;
  const previewKey = `${groupId}/${option.id}/preview`;

  return (
    <div className="rounded-lg border border-gray-800 bg-gray-900 p-3 space-y-3">
      {/* Label row + default radio + remove */}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onSetDefault}
          title={option.is_default ? "Default option" : "Set as default"}
          className={`w-4 h-4 rounded-full border-2 flex-shrink-0 transition ${
            option.is_default ? "border-orange-500 bg-orange-500" : "border-gray-600 hover:border-orange-400"
          }`}
        />
        <input
          type="text"
          value={option.label}
          onChange={(e) => onUpdate({ label: e.target.value })}
          placeholder="Option label (e.g. Matte Black)"
          style={inputStyle}
          className={inputCls + " flex-1 text-sm py-1.5"}
        />
        <button
          type="button"
          onClick={onRemove}
          disabled={isOnly}
          title="Remove option"
          className="text-gray-600 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition text-base leading-none flex-shrink-0"
        >
          ✕
        </button>
      </div>

      {/* Swatch + preview + price */}
      <div className="flex gap-3 items-start">
        {/* Swatch upload */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-600">Swatch</span>
          <div
            onClick={() => uploadingKey !== swatchKey && swatchRef.current?.click()}
            className={`w-10 h-10 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition overflow-hidden ${
              option.swatch_url ? "border-green-700" : "border-gray-700 hover:border-gray-500"
            } ${uploadingKey === swatchKey ? "pointer-events-none" : ""}`}
          >
            {uploadingKey === swatchKey ? (
              <span className="text-[10px] text-orange-400">…</span>
            ) : option.swatch_url ? (
              <img src={option.swatch_url} alt="swatch" className="w-full h-full object-cover" />
            ) : (
              <span className="text-gray-600 text-xs">🎨</span>
            )}
          </div>
          <input ref={swatchRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadSwatch(f); e.target.value = ""; }} />
        </div>

        {/* Preview upload */}
        <div className="flex flex-col items-center gap-1">
          <span className="text-[10px] text-gray-600">Preview</span>
          <div
            onClick={() => uploadingKey !== previewKey && previewRef.current?.click()}
            className={`w-10 h-10 rounded-lg border-2 border-dashed flex items-center justify-center cursor-pointer transition overflow-hidden ${
              option.preview_url ? "border-green-700" : "border-gray-700 hover:border-gray-500"
            } ${uploadingKey === previewKey ? "pointer-events-none" : ""}`}
          >
            {uploadingKey === previewKey ? (
              <span className="text-[10px] text-orange-400">…</span>
            ) : option.preview_url ? (
              <img src={option.preview_url} alt="preview" className="w-full h-full object-cover" />
            ) : (
              <span className="text-gray-600 text-xs">👁️</span>
            )}
          </div>
          <input ref={previewRef} type="file" accept="image/*" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadPreview(f); e.target.value = ""; }} />
        </div>

        {/* Price delta */}
        <div className="flex-1 space-y-1">
          <span className="text-[10px] text-gray-600">Price adjustment</span>
          <div className="flex items-center gap-1">
            <span className="text-xs text-gray-500">+$</span>
            <input
              type="number"
              step="0.01"
              value={option.price_delta_cents === 0 ? "" : String(option.price_delta_cents / 100)}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                onUpdate({ price_delta_cents: isNaN(v) ? 0 : Math.round(v * 100) });
              }}
              placeholder="0.00"
              style={inputStyle}
              className={inputCls + " text-sm py-1.5"}
            />
          </div>
          {option.price_delta_cents !== 0 && (
            <span className="text-xs" style={{ color: "#FF6B35" }}>
              {option.price_delta_cents > 0 ? "+" : ""}${(option.price_delta_cents / 100).toFixed(2)} from base
            </span>
          )}
        </div>
      </div>

      {/* Advanced: per-variant 3D asset overrides */}
      <button
        type="button"
        onClick={() => setShowAdvanced((v) => !v)}
        className="text-xs text-gray-600 hover:text-gray-400 transition"
      >
        {showAdvanced ? "▲ Hide" : "▼ Advanced"} — per-variant 3D model override
      </button>
      {showAdvanced && (
        <div className="rounded-lg border border-gray-800 bg-gray-950 p-3 text-xs text-gray-500 space-y-1">
          <p>GLB override: {option.asset_overrides.glb_url ?? "none (uses base model)"}</p>
          <p>USDZ override: {option.asset_overrides.usdz_url ?? "none (uses base model)"}</p>
          <p className="text-gray-600 mt-1">Per-variant model uploads are handled via the pipeline after initial 3D processing. Leave blank to use material swap on the base model (recommended for v1).</p>
        </div>
      )}

      {groupType === "burner_type" && (
        <div className="space-y-2 pt-1 border-t border-gray-800">
          <div className="space-y-1">
            <FieldLabel>Reference video (optional)</FieldLabel>
            <input
              type="text"
              value={option.reference_video_url ?? ""}
              onChange={(e) => onUpdate({ reference_video_url: e.target.value || null })}
              placeholder="https://…"
              style={inputStyle}
              className={inputCls + " text-sm py-1.5"}
            />
            <p className="text-[10px] text-gray-600">
              Shown read-only in the QA/review view for this variant. Purely informational — never processed.
            </p>
          </div>
          <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={option.has_distinct_geometry}
              onChange={(e) => onUpdate({ has_distinct_geometry: e.target.checked })}
              className="rounded"
            />
            This burner type has a different shape
          </label>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Step5Configuration({
  state,
  update,
  onBack,
  onNext,
  onSkip,
  jwt,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
  onBack: () => void;
  onNext: () => void;
  onSkip?: () => void;
  jwt: string;
}) {
  const { toast } = useToast();
  const [uploadingKey, setUploadingKey] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [previewSelected, setPreviewSelected] = useState<Record<string, string>>({});

  const groups = state.variants.groups;

  // ── Load: fetch existing variant_groups/variant_options for this product ──
  useEffect(() => {
    if (!state.productId) return;
    (async () => {
      const { data: groupRows, error: groupErr } = await supabase
        .from("variant_groups")
        .select("id, group_type, label, is_required, display_order, legacy_slug")
        .eq("product_id", state.productId)
        .order("display_order");
      if (groupErr || !groupRows) return;

      const groupIds = groupRows.map((g) => g.id);
      const { data: optionRows } = groupIds.length
        ? await supabase
            .from("variant_options")
            .select("id, variant_group_id, label, swatch_image_url, preview_image_url, submesh_url, usdz_override_url, reference_video_url, has_distinct_geometry, is_default, price_delta_cents, display_order, legacy_slug")
            .in("variant_group_id", groupIds)
            .order("display_order")
        : { data: [] };

      const loaded: VariantGroup[] = groupRows.map((g) => ({
        id: g.legacy_slug ?? g.id,
        label: g.label,
        type: "single_select",
        required: g.is_required,
        group_type: g.group_type as VariantGroupType,
        options: (optionRows ?? [])
          .filter((o) => o.variant_group_id === g.id)
          .map((o) => ({
            id: o.legacy_slug ?? o.id,
            label: o.label,
            swatch_url: o.swatch_image_url,
            preview_url: o.preview_image_url,
            price_delta_cents: o.price_delta_cents,
            is_default: o.is_default,
            asset_overrides: { glb_url: o.submesh_url, usdz_url: o.usdz_override_url },
            reference_video_url: o.reference_video_url,
            has_distinct_geometry: o.has_distinct_geometry,
          })),
      }));
      update({ variants: { groups: loaded } });
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.productId]);

  // Keep preview selection in sync with defaults when groups change
  useEffect(() => {
    const next: Record<string, string> = {};
    for (const g of groups) {
      const def = g.options.find((o) => o.is_default) ?? g.options[0];
      if (def) next[g.id] = def.id;
    }
    setPreviewSelected((prev) => {
      const merged: Record<string, string> = {};
      for (const g of groups) {
        merged[g.id] = prev[g.id] && g.options.some((o) => o.id === prev[g.id])
          ? prev[g.id]
          : (g.options.find((o) => o.is_default) ?? g.options[0])?.id ?? "";
      }
      return merged;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups.length]);

  function setGroups(gs: VariantGroup[]) {
    update({ variants: { groups: gs } });
  }

  function addGroup(groupType: VariantGroupType) {
    const newG: VariantGroup = {
      id: `grp_${Date.now()}`,
      label: "",
      type: "single_select",
      required: true,
      group_type: groupType,
      options: [
        {
          id: `opt_${Date.now()}`,
          label: "",
          swatch_url: null,
          preview_url: null,
          price_delta_cents: 0,
          is_default: true,
          asset_overrides: { glb_url: null, usdz_url: null },
          reference_video_url: null,
          has_distinct_geometry: false,
        },
      ],
    };
    setGroups([...groups, newG]);
  }

  function removeGroup(gi: number) {
    setGroups(groups.filter((_, i) => i !== gi));
  }

  function moveGroup(gi: number, dir: -1 | 1) {
    const next = [...groups];
    const to = gi + dir;
    if (to < 0 || to >= next.length) return;
    [next[gi], next[to]] = [next[to], next[gi]];
    setGroups(next);
  }

  function updateGroup(gi: number, patch: Partial<VariantGroup>) {
    setGroups(groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
  }

  function addOption(gi: number) {
    const g = groups[gi];
    const newOpt: VariantOption = {
      id: `opt_${Date.now()}`,
      label: "",
      swatch_url: null,
      preview_url: null,
      price_delta_cents: 0,
      is_default: false,
      asset_overrides: { glb_url: null, usdz_url: null },
      reference_video_url: null,
      has_distinct_geometry: false,
    };
    updateGroup(gi, { options: [...g.options, newOpt] });
  }

  function removeOption(gi: number, oi: number) {
    const g = groups[gi];
    if (g.options.length <= 1) return;
    const next = g.options.filter((_, i) => i !== oi);
    if (g.options[oi].is_default) next[0] = { ...next[0], is_default: true };
    updateGroup(gi, { options: next });
  }

  function updateOption(gi: number, oi: number, patch: Partial<VariantOption>) {
    const g = groups[gi];
    updateGroup(gi, { options: g.options.map((o, i) => (i === oi ? { ...o, ...patch } : o)) });
  }

  function setDefault(gi: number, oi: number) {
    const g = groups[gi];
    updateGroup(gi, { options: g.options.map((o, i) => ({ ...o, is_default: i === oi })) });
    setPreviewSelected((prev) => ({ ...prev, [g.id]: g.options[oi].id }));
  }

  async function uploadVariantImage(
    groupId: string,
    optionId: string,
    imageType: "swatch" | "preview",
    file: File,
    gi: number,
    oi: number
  ) {
    const ext = file.name.split(".").pop()?.toLowerCase() ?? "png";
    const key = `variants/${state.wizardId}/${groupId}/${optionId}/${imageType}.${ext}`;
    setUploadingKey(`${groupId}/${optionId}/${imageType}`);
    try {
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(key, file.type || "image/png", jwt);
      await xhrPut(uploadUrl, file, file.type || "image/png", () => {});
      updateOption(gi, oi, imageType === "swatch" ? { swatch_url: publicUrl } : { preview_url: publicUrl });
    } catch (err) {
      toast({ title: "Upload failed", description: err instanceof Error ? err.message : "Upload error" });
    } finally {
      setUploadingKey(null);
    }
  }

  function validate(): string[] {
    const errs: string[] = [];
    for (const [gi, g] of groups.entries()) {
      if (!g.label.trim()) errs.push(`Group ${gi + 1}: label is required`);
      if (g.options.length === 0) errs.push(`Group "${g.label || gi + 1}": at least one option is required`);
      const defaultCount = g.options.filter((o) => o.is_default).length;
      if (defaultCount !== 1) errs.push(`Group "${g.label || gi + 1}": exactly one default option required (${defaultCount} found)`);
      for (const [oi, opt] of g.options.entries()) {
        if (!opt.label.trim()) errs.push(`Group "${g.label || gi + 1}", Option ${oi + 1}: label is required`);
      }
    }
    return errs;
  }

  function handleNext() {
    const errs = validate();
    if (errs.length > 0) { setErrors(errs); return; }
    setErrors([]);
    onNext();
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Step 5 — Configuration</h2>
        <p className="mt-1 text-sm text-gray-400">
          Define configurable options consumers will see in the AR app (e.g. finish, colour). Skippable — most products have none.
        </p>
      </div>

      <div className="flex gap-5">
        {/* ── Left: group editor ── */}
        <div className="flex-1 min-w-0 space-y-4">
          {groups.length === 0 && (
            <div className="rounded-xl border border-dashed border-gray-800 p-6 text-center space-y-2">
              <p className="text-gray-500 text-sm">No variant groups yet.</p>
              <p className="text-gray-700 text-xs">
                Use this step if your product has different finishes, colours, or configurations.
                Skip it if the product is a single variant.
              </p>
            </div>
          )}

          {groups.map((g, gi) => (
            <div key={g.id} className="rounded-xl border border-gray-800 bg-gray-900/40 p-4 space-y-4">
              {/* Group header */}
              <div className="flex items-start gap-3">
                <div className="flex-1 space-y-1">
                  <FieldLabel>Group Label *</FieldLabel>
                  <input
                    type="text"
                    value={g.label}
                    onChange={(e) => updateGroup(gi, { label: e.target.value })}
                    placeholder="e.g. Finish, Colour, Fuel Type"
                    style={inputStyle}
                    className={inputCls + " text-sm"}
                  />
                </div>
                <div className="flex flex-col gap-1 pt-5">
                  <button type="button" onClick={() => moveGroup(gi, -1)} disabled={gi === 0} className="text-gray-600 hover:text-gray-400 disabled:opacity-20 text-xs transition">▲</button>
                  <button type="button" onClick={() => moveGroup(gi, 1)} disabled={gi === groups.length - 1} className="text-gray-600 hover:text-gray-400 disabled:opacity-20 text-xs transition">▼</button>
                </div>
                <div className="flex items-center gap-3 pt-5">
                  <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer select-none whitespace-nowrap">
                    <input
                      type="checkbox"
                      checked={g.required}
                      onChange={(e) => updateGroup(gi, { required: e.target.checked })}
                      className="rounded"
                    />
                    Required
                  </label>
                  <button type="button" onClick={() => removeGroup(gi)} className="text-xs text-gray-600 hover:text-red-400 transition whitespace-nowrap">
                    Remove
                  </button>
                </div>
              </div>

              {/* Options */}
              <div className="space-y-2">
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                  Options — ● = default
                </p>
                {g.options.map((opt, oi) => (
                  <VariantOptionRow
                    key={opt.id}
                    option={opt}
                    groupId={g.id}
                    groupType={g.group_type}
                    isOnly={g.options.length === 1}
                    onUpdate={(patch) => updateOption(gi, oi, patch)}
                    onRemove={() => removeOption(gi, oi)}
                    onSetDefault={() => setDefault(gi, oi)}
                    onUploadSwatch={(f) => uploadVariantImage(g.id, opt.id, "swatch", f, gi, oi)}
                    onUploadPreview={(f) => uploadVariantImage(g.id, opt.id, "preview", f, gi, oi)}
                    uploadingKey={uploadingKey}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => addOption(gi)}
                disabled={!!uploadingKey}
                className="text-xs transition disabled:opacity-40"
                style={{ color: "#FF6B35" }}
              >
                + Add option
              </button>
            </div>
          ))}

          <div className="flex gap-2">
            <button type="button" onClick={() => addGroup("exterior_finish")}
              className="flex-1 rounded-xl border border-dashed border-gray-700 py-3 text-sm text-gray-400 hover:border-orange-500 hover:text-orange-400 transition">
              + Exterior Finish
            </button>
            {BURNER_INTERIOR_GATE_CATEGORIES.has(state.category) && (
              <>
                <button type="button" onClick={() => addGroup("burner_type")}
                  className="flex-1 rounded-xl border border-dashed border-gray-700 py-3 text-sm text-gray-400 hover:border-orange-500 hover:text-orange-400 transition">
                  + Burner Type
                </button>
                <button type="button" onClick={() => addGroup("interior_panel")}
                  className="flex-1 rounded-xl border border-dashed border-gray-700 py-3 text-sm text-gray-400 hover:border-orange-500 hover:text-orange-400 transition">
                  + Interior Panel
                </button>
              </>
            )}
          </div>

          {errors.length > 0 && (
            <div className="rounded-lg border border-red-900/40 bg-red-900/20 p-3 space-y-1">
              {errors.map((e, i) => <p key={i} className="text-xs text-red-400">{e}</p>)}
            </div>
          )}
        </div>

        {/* ── Right: mobile preview pane ── */}
        <div className="hidden sm:flex flex-col items-center gap-2 flex-shrink-0">
          <p className="text-[10px] text-gray-600 uppercase tracking-widest">Preview</p>
          <MobilePreview
            productName={state.name}
            heroUrl={state.heroUrl}
            groups={groups}
            selected={previewSelected}
            onSelect={(gid, oid) => setPreviewSelected((prev) => ({ ...prev, [gid]: oid }))}
            basePrice={state.base_price}
          />
        </div>
      </div>

      {/* Nav */}
      <div className="flex justify-between pt-4 border-t border-gray-800">
        <button onClick={onBack} className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 transition">
          ← Back
        </button>
        <div className="flex gap-3">
          {groups.length === 0 && (
            <button
              onClick={() => { setErrors([]); onSkip ? onSkip() : onNext(); }}
              className="rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-400 hover:bg-gray-800 transition"
            >
              Skip — no variants →
            </button>
          )}
          <button
            onClick={handleNext}
            disabled={!!uploadingKey}
            className="rounded-lg px-5 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition"
            style={{ backgroundColor: "#FF6B35" }}
          >
            {uploadingKey ? "Uploading…" : groups.length > 0 ? "Save & Continue →" : "Continue →"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 6 — Photos                                                      */
/* ------------------------------------------------------------------ */

interface PhotoSlotProps {
  angle: string;
  index: number;
  productId: string;
  url: string;
  jwt: string;
  onUpload: (index: number, url: string) => void;
}

function PhotoSlot({ angle, index, productId, url, jwt, onUpload }: PhotoSlotProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [slotError, setSlotError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setSlotError(null);
    setUploading(true);
    setProgress(0);
    try {
      const ext = file.name.split(".").pop() ?? "jpg";
      const s3Key = `raw-assets/${productId}/photos/${angle}/${Date.now()}.${ext}`;
      const { uploadUrl, publicUrl } = await getWizardUploadUrl(s3Key, file.type || "image/jpeg", jwt);
      await xhrPut(uploadUrl, file, file.type || "image/jpeg", setProgress);
      onUpload(index, publicUrl);
    } catch (err) {
      setSlotError(err instanceof Error ? err.message : "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">{angle}</span>
      <div
        onClick={() => !uploading && fileRef.current?.click()}
        className={[
          "flex flex-col items-center justify-center gap-1.5 rounded-lg border-2 border-dashed p-3 text-center cursor-pointer transition select-none min-h-[100px]",
          url ? "border-green-600 bg-green-900/10" : "border-gray-700 bg-gray-900/60 hover:border-gray-500",
          uploading ? "pointer-events-none opacity-60" : "",
        ].join(" ")}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          disabled={uploading}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
        />
        {url ? (
          <img src={url} alt={angle} className="h-16 w-full object-cover rounded" />
        ) : uploading ? (
          <>
            <span className="text-xs text-orange-400">Uploading… {progress}%</span>
            <div className="h-1 w-full rounded-full bg-gray-800 overflow-hidden">
              <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${progress}%` }} />
            </div>
          </>
        ) : (
          <>
            <span className="text-xl text-gray-600">🖼️</span>
            <span className="text-xs text-gray-500">{angle}</span>
          </>
        )}
      </div>
      {slotError && <p className="text-xs text-red-400 mt-0.5">{slotError}</p>}
    </div>
  );
}

function Step6Photos({
  state,
  setState,
  onNext,
  onBack,
  onSkip,
  jwt,
}: {
  state: WizardState;
  setState: (updater: (prev: WizardState) => WizardState) => void;
  onNext: () => void;
  onBack: () => void;
  onSkip?: () => void;
  jwt: string;
}) {
  const hasAny = state.photoUrls.some((url) => url !== "");

  function handleUpload(index: number, url: string) {
    setState((prev) => {
      const next = [...prev.photoUrls];
      next[index] = url;
      return { ...prev, photoUrls: next };
    });
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Step 6 — Photos</h2>
        <p className="mt-1 text-sm text-gray-400">
          Upload product photos from multiple angles. At least one is required to continue.
          Photos upload immediately on selection.
        </p>
        <p className="mt-1 text-sm text-gray-400">
          The pipeline automatically attempts to remove the background before 3D reconstruction,
          but it isn&apos;t guaranteed to succeed on busy scenes. Prefer photos where the product
          fills the frame against a plain wall, floor, or backdrop — avoid other furniture, mantels,
          or room decor in shot where possible. Cleaner source photos mean a lower chance of
          reconstruction picking up background clutter as part of the model.
        </p>
      </div>

      {!state.productId && (
        <p className="text-xs text-yellow-600">
          Product record not created yet — complete Step 2 first.
        </p>
      )}

      <div className="grid grid-cols-4 gap-3">
        {PHOTO_ANGLES.map((angle, i) => (
          <PhotoSlot
            key={angle}
            angle={angle}
            index={i}
            productId={state.productId ?? ""}
            url={state.photoUrls[i] ?? ""}
            jwt={jwt}
            onUpload={handleUpload}
          />
        ))}
      </div>

      <NavButtons
        onBack={onBack}
        onNext={onNext}
        onSkip={onSkip}
        skipLabel="Skip — add photos later →"
        nextLabel="Save Photos & Continue →"
        nextDisabled={!hasAny || !state.productId}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Step 7 — Review & Publish                                            */
/* ------------------------------------------------------------------ */

interface PipelineSnapshot {
  pbr_status:          string | null;
  geometry_status:     string | null;
  glb_assembly_status: string | null;
  pbr_confidence:      number | null;
}

function PipelineStatusPanel({ productId, jwt }: { productId: string; jwt: string }) {
  const [snap, setSnap] = useState<PipelineSnapshot | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      try {
        const res = await fetch(
          `${SB_URL}/rest/v1/products?id=eq.${productId}&select=pbr_status,geometry_status,glb_assembly_status,pbr_confidence&limit=1`,
          { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
        );
        if (!res.ok || cancelled) return;
        const data = await res.json();
        if (data[0]) setSnap(data[0]);
        const s = data[0] as PipelineSnapshot | undefined;
        const active = s && (
          s.pbr_status === "pending" || s.pbr_status === "processing" ||
          s.geometry_status === "pending" || s.geometry_status === "processing" ||
          s.glb_assembly_status === "pending" || s.glb_assembly_status === "processing"
        );
        if (active && !cancelled) setTimeout(poll, 5000);
      } catch { /* non-fatal */ }
    }

    poll();
    return () => { cancelled = true; };
  }, [productId, jwt]);

  function StageRow({ label, status, confidence }: { label: string; status: string | null; confidence?: number | null }) {
    let icon: React.ReactNode;
    let textClass: string;
    if (!status || status === "pending") {
      icon = <span className="inline-block w-3.5 h-3.5 rounded-full border border-gray-600 bg-gray-800" />;
      textClass = "text-gray-500";
    } else if (status === "processing") {
      icon = (
        <svg className="w-3.5 h-3.5 animate-spin text-blue-400" viewBox="0 0 24 24" fill="none">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
        </svg>
      );
      textClass = "text-blue-400";
    } else if (status === "complete") {
      icon = <span className="text-green-400 font-bold text-sm leading-none">✓</span>;
      textClass = "text-green-400";
    } else if (status === "complete_fallback") {
      // Maps exist and are usable — generated via algorithmic fallback, not
      // real Stability output (e.g. the account ran out of credits). Not a
      // failure, so not the red ✗ below — but distinct from a real ✓.
      icon = <span className="text-amber-400 font-bold text-sm leading-none">⚠</span>;
      textClass = "text-amber-400";
    } else {
      icon = <span className="text-red-400 font-bold text-sm leading-none">✗</span>;
      textClass = "text-red-400";
    }

    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="flex items-center justify-center w-4 h-4 shrink-0">{icon}</span>
        <span className={textClass}>{label}</span>
        {confidence != null && (
          <span className="ml-auto text-xs text-gray-500">{(confidence * 100).toFixed(0)}% confidence</span>
        )}
        {status && status !== "pending" && confidence == null && (
          <span className={`ml-auto text-xs capitalize ${textClass}`}>{status}</span>
        )}
        {(!status || status === "pending") && (
          <span className="ml-auto text-xs text-gray-600">Pending</span>
        )}
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 space-y-3">
      <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Pipeline Status</p>
      {snap === null ? (
        <p className="text-xs text-gray-600 animate-pulse">Loading pipeline status…</p>
      ) : (
        <div className="space-y-2">
          <StageRow label="PBR Materials" status={snap.pbr_status} confidence={snap.pbr_confidence} />
          <StageRow label="Geometry" status={snap.geometry_status} />
          <StageRow label="GLB Assembly" status={snap.glb_assembly_status} />
        </div>
      )}
      <p className="text-xs text-gray-600 mt-1">Auto-refreshes every 5 seconds while pipeline is active.</p>
    </div>
  );
}

function ReviewCard({ state }: { state: WizardState }) {
  const categoryName = categoryLabel(state.category);
  const specs = state.consumerSpecs;

  return (
    <div className="rounded-2xl border border-gray-700 bg-gray-900 overflow-hidden">
      {/* Hero image or placeholder */}
      <div className="h-48 bg-gray-800 flex items-center justify-center overflow-hidden">
        {state.heroUrl ? (
          <img src={state.heroUrl} alt={state.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-5xl text-gray-700">📦</span>
        )}
      </div>

      <div className="p-4 space-y-3">
        {/* Name + AR badge */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-base leading-tight">{state.name || "Unnamed Product"}</h3>
          <span className={[
            "shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
            isAssetPipelineFinished(state.assetStatus)
              ? "border-green-800 bg-green-900/40 text-green-400"
              : state.assetStatus === "processing" || state.assetStatus === "pending"
              ? "border-orange-800 bg-orange-900/40 text-orange-400"
              : "border-gray-700 bg-gray-800 text-gray-500",
          ].join(" ")}>
            {isAssetPipelineFinished(state.assetStatus) ? "AR Ready" : state.assetStatus === "processing" ? "AR Processing" : "No AR"}
          </span>
        </div>

        <p className="text-xs text-orange-400">{categoryName}</p>

        {state.description && (
          <p className="text-sm text-gray-300 line-clamp-3">{state.description}</p>
        )}

        {/* Key specs */}
        {specs && (
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 pt-2 border-t border-gray-800">
            {specs.dimensions?.height && (
              <div className="flex justify-between text-xs"><span className="text-gray-500">Height</span><span className="text-gray-300">{specs.dimensions.height}</span></div>
            )}
            {specs.dimensions?.width && (
              <div className="flex justify-between text-xs"><span className="text-gray-500">Width</span><span className="text-gray-300">{specs.dimensions.width}</span></div>
            )}
            {specs.btu_output && (
              <div className="flex justify-between text-xs"><span className="text-gray-500">BTU</span><span className="text-gray-300">{specs.btu_output}</span></div>
            )}
            {specs.fuel_type && (
              <div className="flex justify-between text-xs"><span className="text-gray-500">Fuel</span><span className="text-gray-300">{specs.fuel_type}</span></div>
            )}
          </div>
        )}

        {/* Certifications */}
        {specs?.certifications && specs.certifications.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {specs.certifications.map((c) => (
              <span key={c} className="rounded border border-gray-700 bg-gray-800 px-1.5 py-0.5 text-xs text-gray-400">{c}</span>
            ))}
          </div>
        )}

        {state.base_price && (
          <p className="text-sm font-semibold text-right text-gray-200">${Number(state.base_price).toLocaleString()}</p>
        )}
      </div>
    </div>
  );
}

function Step7Review({
  state,
  update,
  onBack,
  onPublish,
  publishing,
  published,
  jwt,
  editProductId,
  onGoToStep1,
}: {
  state: WizardState;
  update: (patch: Partial<WizardState>) => void;
  onBack: () => void;
  onPublish: (active: boolean) => void;
  publishing: boolean;
  published: boolean;
  jwt: string;
  editProductId?: string;
  onGoToStep1: () => void;
}) {
  const specs = state.consumerSpecs;
  const clearances = state.clearances;
  const [specsOpen, setSpecsOpen] = useState(false);
  const [clearancesOpen, setClearancesOpen] = useState(false);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">
          Step 7 — Review &amp; {state.productType === "parametric" ? "Generate" : "Publish"}
        </h2>
        <p className="mt-1 text-sm text-gray-400">
          {state.productType === "parametric"
            ? "Review the parameters. Generating creates a draft with a preview mesh — approve it on the Parametric Drafts page to create the product."
            : "Review the product card and publish when ready."}
        </p>
      </div>

      {/* Preview card */}
      <ReviewCard state={state} />

      {/* Parameters recap — the numbers that become the geometry. */}
      {state.productType === "parametric" && (
        <div className="rounded-xl border border-gray-800 p-4">
          <p className="mb-3 text-sm font-medium text-gray-300">Parameters</p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
            {PERGOLA_PARAM_FIELDS.map((f) => (
              <div key={f.key} className="flex justify-between gap-2 text-sm">
                <dt className="text-gray-500">{f.label}</dt>
                <dd className="text-gray-200 tabular-nums">
                  {String(state.pergolaParams[f.key])}{f.unit && ` ${f.unit}`}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}

      {/* Consumer specs preview */}
      {specs && (
        <div className="rounded-xl border border-gray-800 overflow-hidden">
          <button
            onClick={() => setSpecsOpen((o) => !o)}
            className="w-full flex items-center justify-between px-4 py-3 text-sm text-gray-300 hover:bg-gray-800/40 transition"
          >
            <span className="font-medium">Consumer Specs Preview</span>
            <span className="text-xs text-gray-500">{specsOpen ? "▲ collapse" : "▼ expand"}</span>
          </button>
          {specsOpen && (
            <div className="px-4 pb-4 space-y-2">
              {specs.key_features && specs.key_features.length > 0 && (
                <ul className="space-y-1">
                  {specs.key_features.map((f, i) => (
                    <li key={i} className="text-sm text-gray-300 flex gap-2">
                      <span className="text-orange-500 shrink-0">•</span>{f}
                    </li>
                  ))}
                </ul>
              )}
              {specs.warranty && (
                <p className="text-sm text-gray-400">Warranty: <span className="text-gray-200">{specs.warranty}</span></p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Clearances (AR overlay) preview */}
      {clearances && (() => {
        const DIRS: [string, number | null, string | null][] = (
          [
            ["Rear",    clearances.rear_mm,    "rear_mm"],
            ["Side",    clearances.side_mm,    "side_mm"],
            ["Front",   clearances.front_mm,   "front_mm"],
            ["Ceiling", clearances.ceiling_mm, "ceiling_mm"],
            ["Floor",   clearances.floor_mm,   "floor_mm"],
          ] as const
        ).map(([label, mm, key]) => {
          // Manufacturer's printed value + unit (clearances.original), verbatim.
          const orig = clearanceOriginal(clearances, key);
          return [label, mm, orig ? formatClearanceOriginal(orig) : null];
        });
        const allNull = DIRS.every(([, v]) => v === null);
        return (
          <div className="rounded-xl border border-gray-800 overflow-hidden">
            {/* Header row — accordion toggle + Edit link as siblings (no nested buttons) */}
            <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-800/20 transition">
              <button
                onClick={() => setClearancesOpen((o) => !o)}
                className="flex items-center gap-2 text-sm text-gray-300 hover:text-gray-100 transition"
              >
                <span className="font-medium">Clearances (AR overlay)</span>
                <span className="text-xs text-gray-500">{clearancesOpen ? "▲" : "▼"}</span>
              </button>
              <button
                type="button"
                onClick={onGoToStep1}
                className="text-xs text-orange-400 hover:text-orange-300 transition"
              >
                Edit →
              </button>
            </div>
            {clearancesOpen && (
              <div className="px-4 pb-4 space-y-3 border-t border-gray-800 pt-3">
                {allNull && (
                  <div className="rounded-lg border border-amber-800/50 bg-amber-900/20 px-3 py-2 text-xs text-amber-400">
                    ⚠ No clearance values were extracted — re-process the spec sheet or enter values manually.
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  {DIRS.map(([label, val, printed]) => (
                    <span
                      key={label}
                      className={[
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                        val !== null
                          ? "border-teal-800/60 bg-teal-900/20 text-teal-300"
                          : "border-gray-700 bg-gray-800/60 text-gray-600",
                      ].join(" ")}
                    >
                      <span className={val !== null ? "text-teal-500/70 font-normal" : "text-gray-700 font-normal"}>
                        {label}
                      </span>
                      {val !== null ? `${val} mm` : "—"}
                      {val !== null && printed && (
                        <span className="text-teal-500/70 font-normal" title="Value and unit as printed on the spec sheet">
                          · printed {printed}
                        </span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Product Dimensions (from spec sheet) */}
      {clearances && (
        <div className="rounded-xl border border-gray-800 bg-gray-900/60 px-4 py-3 space-y-2">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">
            Product Dimensions (from spec sheet)
          </p>
          <div className="flex flex-wrap gap-2">
            {(
              [
                ["Width",  clearances.width_mm  ?? null],
                ["Depth",  clearances.depth_mm  ?? null],
                ["Height", clearances.height_mm ?? null],
              ] as [string, number | null][]
            ).map(([label, val]) => (
              <span
                key={label}
                className={[
                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
                  val !== null
                    ? "border-teal-800/60 bg-teal-900/20 text-teal-300"
                    : "border-gray-700 bg-gray-800/60 text-gray-600",
                ].join(" ")}
              >
                <span className={val !== null ? "text-teal-500/70 font-normal" : "text-gray-700 font-normal"}>
                  {label}
                </span>
                {val !== null ? `${val} mm` : "Not found"}
              </span>
            ))}
          </div>
          <p className="text-xs text-gray-600">
            Used for AR clearance accuracy. Edit after saving if incorrect.
          </p>
        </div>
      )}

      {/* Checklist */}
      <div className="rounded-xl border border-gray-800 bg-gray-900/60 p-4 space-y-2">
        <p className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-2">Checklist</p>
        {[
          { label: "Product name", done: !!state.name },
          { label: "Category", done: !!state.category },
          { label: "Manufacturer", done: !!state.manufacturerId },
          { label: "Spec sheet processed", done: !!state.consumerSpecs },
          { label: "Hero image", done: !!state.heroUrl },
          { label: "3D model uploaded", done: !!state.glbFile },
          { label: "AR model ready", done: isAssetPipelineFinished(state.assetStatus) },
        ].map(({ label, done }) => (
          <div key={label} className="flex items-center gap-2 text-sm">
            <span className={done ? "text-green-400" : "text-gray-600"}>{done ? "✓" : "○"}</span>
            <span className={done ? "text-gray-200" : "text-gray-500"}>{label}</span>
          </div>
        ))}
      </div>

      {/* Skipped steps warning */}
      {state.skippedSteps.length > 0 && (() => {
        const STEP_NAMES: Record<number, string> = { 3: "Media (images)", 4: "3D Model", 5: "Configuration", 6: "Photos" };
        return (
          <div className="rounded-xl border border-yellow-900/40 bg-yellow-900/10 px-4 py-3 space-y-1.5">
            <p className="text-sm font-medium text-yellow-400">Some steps were skipped</p>
            <ul className="space-y-0.5">
              {state.skippedSteps.map((n) => (
                <li key={n} className="text-xs text-yellow-600">• Step {n}: {STEP_NAMES[n] ?? `Step ${n}`}</li>
              ))}
            </ul>
            <p className="text-xs text-gray-500">Product will be saved as <span className="text-yellow-400">draft</span>. Complete missing sections in the product editor after saving.</p>
          </div>
        );
      })()}

      {/* Pipeline status (shown after publish) */}
      {published && state.productId && state.productType !== "parametric" && (
        <PipelineStatusPanel productId={state.productId} jwt={jwt} />
      )}

      {/* Generated — the parametric branch ends at a draft, not a product. */}
      {published && state.productType === "parametric" && (
        <div className="rounded-xl border border-green-800 bg-green-900/20 p-4">
          <p className="text-sm text-green-300">Draft created and preview generated.</p>
          <p className="mt-1 text-xs text-gray-400">
            Nothing is live yet. Approve it on the Parametric Drafts page — that creates the
            product, generates the mesh and bakes the shadow atlas.
          </p>
          <Link
            to="/admin/parametric-drafts"
            className="mt-3 inline-block rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white hover:bg-green-600 transition"
          >
            Go to Parametric Drafts
          </Link>
        </div>
      )}

      {/* Publish actions */}
      {!published && (
        <div className="flex flex-col gap-3 pt-2">
          <button
            onClick={() => onPublish(true)}
            disabled={publishing}
            className="w-full rounded-lg bg-orange-600 py-3 text-sm font-semibold text-white hover:bg-orange-700 disabled:opacity-40 disabled:cursor-not-allowed transition"
          >
            {state.productType === "parametric"
              ? (publishing ? "Generating…" : "Generate Draft & Preview")
              : (publishing ? "Publishing…" : state.skippedSteps.length > 0 ? "Save as Draft" : "Publish Product")}
          </button>
          {/* Active/draft is not a choice on the parametric branch: every
              generated variant is created inactive (Non-negotiable 10), and the
              product does not exist until the draft is approved. */}
          {state.productType !== "parametric" && state.skippedSteps.length === 0 && (
            <button
              onClick={() => onPublish(false)}
              disabled={publishing}
              className="w-full rounded-lg border border-gray-700 py-2.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40 transition"
            >
              Save as Draft
            </button>
          )}
        </div>
      )}

      <NavButtons showBack onBack={onBack} onNext={undefined} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Bulk Import Modal                                                    */
/* ------------------------------------------------------------------ */

interface CsvImportRow {
  name: string;
  category: string;
  description: string;
  base_price: string;
  manufacturer_id: string;
  rowNum: number;
  valid: boolean;
  errors: string[];
}

const VALID_IMPORT_CATEGORIES = new Set(ALL_CATEGORIES.map((c) => c.value));

const CSV_IMPORT_TEMPLATE =
  "name,category,description,base_price,manufacturer_id\n" +
  "Example Fireplace,gas_fireplace,A beautiful gas fireplace,2999,\n";

function parseImportRow(raw: Record<string, string>, rowNum: number): CsvImportRow {
  const name         = raw["name"]?.trim()            ?? "";
  const category     = raw["category"]?.trim()        ?? "";
  const description  = raw["description"]?.trim()     ?? "";
  const base_price   = raw["base_price"]?.trim()      ?? "";
  const manufacturer_id = raw["manufacturer_id"]?.trim() ?? "";

  const errors: string[] = [];
  if (!name)     errors.push("Missing name");
  if (!category) errors.push("Missing category");
  else if (!VALID_IMPORT_CATEGORIES.has(category))
    errors.push(`Unknown category "${category}"`);
  if (base_price && isNaN(parseFloat(base_price)))
    errors.push("Invalid price — must be a number");

  return { name, category, description, base_price, manufacturer_id, rowNum, valid: errors.length === 0, errors };
}

function BulkImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { toast } = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [phase, setPhase]       = useState<"upload" | "preview" | "importing" | "done">("upload");
  const [rows,  setRows]        = useState<CsvImportRow[]>([]);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [results,  setResults]  = useState({ success: 0, failed: 0 });

  // Reset to upload phase whenever the modal is opened
  useEffect(() => {
    if (open) {
      setPhase("upload");
      setRows([]);
      setProgress({ done: 0, total: 0 });
      setResults({ success: 0, failed: 0 });
    }
  }, [open]);

  // Stable object-URL for the downloadable CSV template
  const templateUrl = useMemo(
    () => URL.createObjectURL(new Blob([CSV_IMPORT_TEMPLATE], { type: "text/csv" })),
    []
  );
  useEffect(() => () => URL.revokeObjectURL(templateUrl), [templateUrl]);

  function handleFile(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const { data } = Papa.parse<Record<string, string>>(text, {
        header: true,
        skipEmptyLines: true,
      });
      setRows(data.map((raw, i) => parseImportRow(raw, i + 1)));
      setPhase("preview");
    };
    reader.readAsText(file);
  }

  const validRows = rows.filter((r) => r.valid);

  async function handleImport() {
    if (validRows.length === 0) return;
    setPhase("importing");
    setProgress({ done: 0, total: validRows.length });
    let success = 0;
    let failed  = 0;
    for (const row of validRows) {
      const { error } = await supabase.from("products").insert({
        name:            row.name,
        category:        row.category,
        description:     row.description  || null,
        base_price:      row.base_price   ? parseFloat(row.base_price) : null,
        manufacturer_id: row.manufacturer_id || null,
        asset_status:    "pending",
        active:          false,
      });
      if (error) failed++; else success++;
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setResults({ success, failed });
    setPhase("done");
    toast({
      title: `${success} product${success !== 1 ? "s" : ""} imported`,
      description: failed > 0
        ? `${failed} row${failed !== 1 ? "s" : ""} failed — see summary below.`
        : "All rows imported successfully.",
    });
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="w-full max-w-2xl rounded-2xl border border-gray-800 bg-gray-950 shadow-2xl">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-800 px-6 py-4">
          <h2 className="text-base font-semibold">Bulk Import Products</h2>
          <button
            onClick={onClose}
            className="text-lg leading-none text-gray-500 hover:text-gray-300 transition"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="max-h-[78vh] overflow-y-auto p-6 space-y-5">

          {/* ── Upload phase ── */}
          {phase === "upload" && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">
                Upload a CSV to create multiple products at once.
                All imports are saved as <span className="text-amber-400">inactive drafts</span> — activate them individually afterwards.
              </p>

              <a
                href={templateUrl}
                download="fyrris_import_template.csv"
                className="inline-flex items-center gap-1.5 text-xs text-orange-400 hover:text-orange-300 transition"
              >
                ↓ Download CSV template
              </a>

              <div
                onClick={() => fileRef.current?.click()}
                className="flex cursor-pointer select-none flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-gray-700 bg-gray-900/60 p-10 text-center transition hover:border-gray-500"
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,text/csv"
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                />
                <span className="text-3xl text-gray-600">📊</span>
                <span className="text-sm text-gray-400">Drop a .csv file here or click to browse</span>
                <span className="text-xs text-gray-600">
                  Required columns: <span className="font-mono text-gray-500">name, category</span> · Optional: <span className="font-mono text-gray-500">description, base_price, manufacturer_id</span>
                </span>
              </div>
            </div>
          )}

          {/* ── Preview phase ── */}
          {phase === "preview" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-300">
                  <span className="font-medium text-green-400">{validRows.length} valid</span>
                  {rows.length - validRows.length > 0 && (
                    <> · <span className="font-medium text-red-400">{rows.length - validRows.length} invalid</span></>
                  )}
                  <span className="text-gray-500"> of {rows.length} rows</span>
                </p>
                <button
                  onClick={() => setPhase("upload")}
                  className="text-xs text-gray-500 hover:text-gray-300 transition"
                >
                  ← Change file
                </button>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-800">
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-800 bg-gray-900/80">
                        <th className="px-3 py-2 text-left font-medium text-gray-500">#</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Name</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Category</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Price</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row) => (
                        <tr
                          key={row.rowNum}
                          className={[
                            "border-b border-gray-800/60 last:border-0",
                            row.valid ? "" : "bg-red-950/30",
                          ].join(" ")}
                        >
                          <td className="px-3 py-2 text-gray-600">{row.rowNum}</td>
                          <td className="max-w-[140px] truncate px-3 py-2 text-gray-200">
                            {row.name || <span className="italic text-gray-600">—</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-400">
                            {row.category || <span className="italic text-gray-600">—</span>}
                          </td>
                          <td className="px-3 py-2 text-gray-400">
                            {row.base_price || <span className="italic text-gray-600">—</span>}
                          </td>
                          <td className="px-3 py-2">
                            {row.valid ? (
                              <span className="text-green-400">✓ Valid</span>
                            ) : (
                              <span className="text-red-400" title={row.errors.join("; ")}>
                                ✗ {row.errors[0]}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {validRows.length > 0 ? (
                <button
                  onClick={handleImport}
                  className="w-full rounded-lg bg-orange-600 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-700"
                >
                  Import {validRows.length} valid product{validRows.length !== 1 ? "s" : ""}
                </button>
              ) : (
                <p className="text-center text-sm text-gray-600">
                  No valid rows to import — fix the errors and re-upload.
                </p>
              )}
            </div>
          )}

          {/* ── Importing phase ── */}
          {phase === "importing" && (
            <div className="space-y-4 py-6 text-center">
              <p className="text-sm text-gray-300">
                Importing {progress.done} / {progress.total}…
              </p>
              <div className="h-2 w-full overflow-hidden rounded-full bg-gray-800">
                <div
                  className="h-full rounded-full bg-orange-500 transition-all duration-300"
                  style={{
                    width: progress.total > 0
                      ? `${Math.round((progress.done / progress.total) * 100)}%`
                      : "0%",
                  }}
                />
              </div>
            </div>
          )}

          {/* ── Done phase ── */}
          {phase === "done" && (
            <div className="space-y-4 py-2">
              <div className="space-y-3 rounded-xl border border-gray-800 bg-gray-900/60 p-5 text-center">
                <p className="text-2xl">{results.failed === 0 ? "✅" : "⚠️"}</p>
                <p className="text-base font-semibold text-gray-100">Import complete</p>
                <div className="flex justify-center gap-6 text-sm">
                  <span className="text-green-400">{results.success} imported</span>
                  {results.failed > 0 && (
                    <span className="text-red-400">{results.failed} failed</span>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  Imported products are saved as inactive drafts.
                  Activate them individually from the Products table.
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-full rounded-lg bg-orange-600 py-2.5 text-sm font-semibold text-white transition hover:bg-orange-700"
              >
                Done
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main Wizard Component                                                */
/* ------------------------------------------------------------------ */

export default function AdminProductWizard() {
  const { toast } = useToast();
  const { session } = useAuth();
  const { id: editProductId } = useParams<{ id: string }>();

  const [step, setStep] = useState(1);
  const [state, setState] = useState<WizardState>(makeInitialState);
  const [publishing, setPublishing] = useState(false);
  const [published, setPublished] = useState(false);
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  const jwt = session?.access_token ?? "";

  // Stable localStorage key: edit mode uses productId from URL, new product uses fixed key
  const draftKey = editProductId ? `wizard_draft_${editProductId}` : "wizard_draft_new";

  const [draftBanner, setDraftBanner] = useState<DraftPayload | null>(null);

  const update = useCallback((patch: Partial<WizardState>) => {
    setState((prev) => ({ ...prev, ...patch }));
  }, []);

  // On mount: check for a recent saved draft and offer to resume
  useEffect(() => {
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const payload = JSON.parse(raw) as DraftPayload;
      const ageMs = Date.now() - new Date(payload.savedAt).getTime();
      if (ageMs > 24 * 60 * 60 * 1000) {
        // Draft is older than 24 h — discard silently
        localStorage.removeItem(draftKey);
        return;
      }
      setDraftBanner(payload);
    } catch {
      // Corrupt data — ignore
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-save on every step / state change.
  // File objects are stripped (non-serialisable); URLs are preserved.
  useEffect(() => {
    // Don't persist a blank initial state (avoids surfacing a spurious banner
    // the next time the wizard is opened after a "Start fresh").
    if (step === 1 && !state.name && !state.consumerSheetUrl) return;
    const serialisable: WizardState = {
      ...state,
      consumerSheetFile: null,
      installSheetFile:  null,
      heroFile:          null,
      textureFiles:      { diffuse: null, normal: null, roughness: null, metalness: null },
      glbFile:           null,
      scanFile:          null,
      cadFile:           null,
    };
    try {
      localStorage.setItem(
        draftKey,
        JSON.stringify({ step, state: serialisable, savedAt: new Date().toISOString() } satisfies DraftPayload)
      );
    } catch { /* storage quota exceeded — ignore */ }
  }, [step, state, draftKey]);

  // Edit mode: load existing product when wizard is opened with a product ID in the URL.
  // Maps source_photos JSONB back to the photoUrls string[8] keyed by PHOTO_ANGLES order.
  useEffect(() => {
    if (!editProductId || !jwt) return;

    fetch(
      `${SB_URL}/rest/v1/products?id=eq.${editProductId}&select=id,name,category,manufacturer_id,source_photos`,
      { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } }
    )
      .then((r) => r.json())
      .then(([product]: [{ id: string; name: string; category: string; manufacturer_id: string; source_photos: { angle: string; url: string }[] | null } | undefined]) => {
        if (!product) return;

        const urls = Array(8).fill("") as string[];
        for (const photo of product.source_photos ?? []) {
          const idx = (PHOTO_ANGLES as readonly string[]).indexOf(photo.angle);
          if (idx !== -1) urls[idx] = photo.url;
        }

        setState((prev) => ({
          ...prev,
          productId: product.id,
          name: product.name ?? "",
          category: product.category ?? "",
          manufacturerId: product.manufacturer_id ?? "",
          photoUrls: urls,
        }));
        setStep(6);
      })
      .catch((err) => {
        console.error("[AdminProductWizard] edit mode load failed:", err);
        toast({ title: "Failed to load product", description: String(err) });
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProductId, jwt]);

  // Called at Step 2 → Step 3: create the draft product record
  // ── Step navigation ────────────────────────────────────────────────
  // A parametric product skips Media Assets, Configuration and Photos: it has
  // no photography, and its finishes come from the swatch library rather than
  // the relational variant tables. Rather than render those steps empty, we
  // step over them. For photogrammetry these are identity functions, so the
  // shipped flow moves exactly as it did.
  const isParametric = state.productType === "parametric";

  // Thin wrappers over src/lib/wizardSteps.ts, which is unit-tested to be the
  // identity (n+1 / n-1) for photogrammetry — the regression guard behind
  // "byte-identical behaviour to today".
  const nextStepFrom = (n: number) => nextApplicableStep(n, state.productType);
  const prevStepFrom = (n: number) => prevApplicableStep(n, state.productType);

  async function advanceFromStep2() {
    // A parametric product is NOT created here. Per Q-A, a generated product is
    // a parametric_asset_drafts row first: draft -> admin approve -> product.
    // approve-parametric-draft is what creates the product row, setting
    // placement_type, geometry_source and material_source correctly and
    // triggering the geometry + shadow-atlas build. Creating a product here too
    // would leave an orphan alongside the draft's own.
    if (isParametric) {
      setStep(nextStepFrom(2));
      return;
    }

    if (!state.productId) {
      // ── Duplicate detection ──────────────────────────────────────────
      if (state.name && state.manufacturerId) {
        try {
          const { data: similar } = await supabase
            .from("products")
            .select("id, name")
            .ilike("name", `%${state.name}%`)
            .eq("manufacturer_id", state.manufacturerId)
            .limit(3);

          if (similar && similar.length > 0) {
            const names = similar.map((p: { id: string; name: string }) => `• ${p.name}`).join("\n");
            const confirmed = window.confirm(
              `Similar product${similar.length > 1 ? "s" : ""} already exist for this manufacturer:\n\n${names}\n\nCreate a new product anyway?`
            );
            if (!confirmed) return; // abort — stay on Step 2
          }
        } catch {
          // Non-fatal — skip the check if the query fails and proceed
        }
      }
      // ────────────────────────────────────────────────────────────────

      try {
        const id = await createProductDraft(state, jwt);
        update({ productId: id });
        // Also save spec sheet + specs to the new product record if already extracted
        const extraFields: Record<string, unknown> = {};
        if (state.consumerSheetUrl) extraFields.spec_sheet_url = state.consumerSheetUrl;
        if (state.installSheetUrl) extraFields.install_spec_sheet_url = state.installSheetUrl;
        if (state.consumerSpecs) extraFields.consumer_specs_json = state.consumerSpecs;
        if (state.installerSpecs) extraFields.install_specs_json = state.installerSpecs;
        if (state.clearances) extraFields.clearances = state.clearances;
        if (Object.keys(extraFields).length > 0) {
          await apiPatch("products", id, extraFields, jwt);
        }
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to create product";
        toast({ title: "Error", description: msg });
        return;
      }
    }
    setStep(nextStepFrom(2));
  }

  // Delete-then-reinsert: variant groups/options per product are single-digit counts,
  // so this is simpler and just as correct as a diff-based upsert, and guarantees no
  // orphaned rows from removed groups/options. products.variants itself is no longer
  // written directly from the wizard — it's regenerated automatically by the Task 1.2
  // trigger as a side effect of these inserts.
  async function saveVariantsToDatabase() {
    if (!state.productId) return;
    const { data: existingGroups } = await supabase
      .from("variant_groups")
      .select("id")
      .eq("product_id", state.productId);
    if (existingGroups?.length) {
      await supabase.from("variant_groups").delete().in("id", existingGroups.map((g) => g.id));
    }

    for (const [gi, g] of state.variants.groups.entries()) {
      const { data: newGroup, error: groupErr } = await supabase
        .from("variant_groups")
        .insert({
          product_id: state.productId,
          group_type: g.group_type,
          label: g.label,
          is_required: g.required,
          display_order: gi,
        })
        .select("id")
        .single();
      if (groupErr || !newGroup) throw new Error(groupErr?.message ?? "Failed to save variant group");

      const optionRows = g.options.map((o, oi) => ({
        variant_group_id: newGroup.id,
        label: o.label,
        swatch_image_url: o.swatch_url,
        preview_image_url: o.preview_url,
        submesh_url: o.asset_overrides.glb_url,
        usdz_override_url: o.asset_overrides.usdz_url,
        reference_video_url: o.reference_video_url,
        has_distinct_geometry: o.has_distinct_geometry,
        is_default: o.is_default,
        price_delta_cents: o.price_delta_cents,
        display_order: oi,
      }));
      if (optionRows.length) {
        const { error: optErr } = await supabase.from("variant_options").insert(optionRows);
        if (optErr) throw new Error(optErr.message);
      }
    }
  }

  // Called at Step 5 → Step 6: save variants to DB before showing Review.
  async function advanceFromStep5() {
    if (state.productId) {
      try {
        await saveVariantsToDatabase();
      } catch (err) {
        const msg = err instanceof Error ? err.message : "Failed to save variants";
        toast({ title: "Variants not persisted", description: msg, variant: "destructive" });
      }
    }
    setStep(6);
  }

  // Called at Step 6 → Step 7: PATCHes source_photos JSONB array to the product record.
  // Uses apiPatch (same JWT-authenticated REST pattern as the rest of the wizard).
  async function advanceFromStep6() {
    if (!state.productId) {
      toast({ title: "No product ID", description: "Complete Step 2 first." });
      return;
    }

    const photos = state.photoUrls
      .map((url, i) => ({ angle: PHOTO_ANGLES[i], url, uploaded_at: new Date().toISOString() }))
      .filter((item) => item.url);

    console.log("[advanceFromStep6] source_photos payload:", photos);

    try {
      await apiPatch("products", state.productId, {
        source_photos: photos,
        photos_uploaded_at: new Date().toISOString(),
      }, jwt);
      console.log("[advanceFromStep6] source_photos PATCH response: success", { photos });

      const { error: triggerError } = await supabase.functions.invoke("trigger-pbr-pipeline", {
        body: { product_id: state.productId },
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (triggerError) {
        console.error("[advanceFromStep6] trigger-pbr-pipeline error:", triggerError);
      } else {
        console.log("[advanceFromStep6] trigger-pbr-pipeline fired successfully");
      }

      const { error: geoError } = await supabase.functions.invoke("trigger-geometry-pipeline", {
        body: { product_id: state.productId },
        headers: { Authorization: `Bearer ${jwt}` },
      });
      if (geoError) {
        console.error("[advanceFromStep6] trigger-geometry-pipeline error:", geoError);
      } else {
        console.log("[advanceFromStep6] trigger-geometry-pipeline fired successfully");
      }

      setStep(7);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save photos";
      console.error("[advanceFromStep6] source_photos PATCH response: error", err);
      toast({ title: "Failed to save photos", description: msg });
    }
  }

  /**
   * Parametric publish — creates a draft, not a product.
   *
   * generate-parametric-preview inserts the parametric_asset_drafts row and
   * runs the real generator for a preview GLB. The admin then approves it on
   * the drafts page, which is what creates the product with the right
   * placement_type / geometry_source / material_source and kicks off the
   * geometry + shadow-atlas build.
   *
   * `parameters` (not `dimensions`) is what selects the pergola branch all the
   * way down the chain — approve-parametric-draft keys on `parameters != null`.
   */
  async function handlePublishParametric() {
    const errors = validatePergolaParams(state.pergolaParams);
    if (Object.keys(errors).length > 0) {
      toast({ title: "Check the parameters", description: "Some values are invalid — go back to step 4." });
      return;
    }
    if (!state.name.trim() || !state.manufacturerId) {
      toast({ title: "Missing details", description: "Name and manufacturer are required." });
      return;
    }

    setPublishing(true);
    try {
      // The manufacturer list is Step2-local, so resolve the name by id here.
      // generate-parametric-preview takes a name, not an id — it predates this
      // wizard path and matches manufacturers by name on the draft row.
      const mRes = await fetch(
        `${SB_URL}/rest/v1/manufacturers?select=name&id=eq.${state.manufacturerId}&limit=1`,
        { headers: { apikey: ANON_KEY, Authorization: `Bearer ${jwt}` } },
      );
      const mRows = await mRes.json().catch(() => []);
      const manufacturerName: string = Array.isArray(mRows) ? (mRows[0]?.name ?? "") : "";
      if (!manufacturerName) throw new Error("Could not resolve the manufacturer name");

      const res = await fetch(`${SB_URL}/functions/v1/generate-parametric-preview`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          apikey: ANON_KEY,
        },
        body: JSON.stringify({
          manufacturer_name: manufacturerName,
          product_name: state.name.trim(),
          sku: state.name.trim(),
          parameters: toPergolaDimensions(state.pergolaParams),
          selected_finishes: ["Bronze"],
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error ?? `Request failed (${res.status})`);

      setPublished(true);
      localStorage.removeItem(draftKey);
      toast({
        title: "Draft created",
        description: "Preview is generating. Approve it on the Parametric Drafts page to create the product.",
      });
    } catch (err) {
      toast({
        title: "Could not create draft",
        description: err instanceof Error ? err.message : "Unknown error",
      });
    } finally {
      setPublishing(false);
    }
  }

  async function handlePublish(active: boolean) {
    if (isParametric) return handlePublishParametric();

    if (!state.productId) {
      toast({ title: "No product created", description: "Something went wrong — product ID missing." });
      return;
    }

    const effectiveActive = state.skippedSteps.length > 0 ? false : active;

    setPublishing(true);
    try {
      const fields: Record<string, unknown> = {
        active: effectiveActive,
        name: state.name.trim(),
        category: state.category,
        manufacturer_id: state.manufacturerId,
      };

      if (state.base_price) fields.base_price = Number(state.base_price);
      if (state.description) fields.description = state.description;
      fields.available_countries = state.availableCountries ?? null;
      if (state.consumerSheetUrl) fields.spec_sheet_url = state.consumerSheetUrl;
      if (state.installSheetUrl) fields.install_spec_sheet_url = state.installSheetUrl;
      if (state.consumerSpecs) fields.consumer_specs_json = state.consumerSpecs;
      if (state.installerSpecs) fields.install_specs_json = state.installerSpecs;
      if (state.clearances) fields.clearances = state.clearances;
      if (state.heroUrl) fields.image_url = state.heroUrl;
      // products.variants is no longer written directly here — it's derived from
      // variant_groups/variant_options (saved at the Step 5 → Step 6 transition) by the
      // Task 1.2 sync trigger. Writing it here too would race/overwrite the trigger's output.

      await apiPatch("products", state.productId, fields, jwt);

      toast({
        title: effectiveActive ? "Product published!" : "Draft saved",
        description: effectiveActive ? `"${state.name}" is now live.` : `"${state.name}" saved as draft.`,
      });

      setPublished(true);
      // Clear the localStorage draft now that the product is persisted
      localStorage.removeItem(draftKey);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to save";
      toast({ title: "Error", description: msg });
    } finally {
      setPublishing(false);
    }
  }

  return (
    <div className="max-w-2xl space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">New Product</h1>
          <p className="mt-1 text-sm text-gray-400">Upload a spec sheet and Claude auto-fills the form.</p>
        </div>
        <button
          onClick={() => setBulkImportOpen(true)}
          className="shrink-0 rounded-lg border border-gray-700 px-4 py-2 text-sm text-gray-300 hover:bg-gray-800 transition"
        >
          Bulk Import
        </button>
      </div>

      {/* Draft resume banner */}
      {draftBanner && (
        <DraftResumeBanner
          savedAt={draftBanner.savedAt}
          onResume={() => {
            setState(draftBanner.state);
            setStep(draftBanner.step);
            setDraftBanner(null);
          }}
          onDiscard={() => {
            localStorage.removeItem(draftKey);
            setDraftBanner(null);
          }}
        />
      )}

      {/* Step indicator */}
      <StepIndicator current={step} skipped={isParametric ? PARAMETRIC_SKIPPED_STEPS : []} />

      {/* Step content */}
      <div className="rounded-2xl border border-gray-800 bg-gray-950 p-6">
        {step === 1 && (
          <Step1
            state={state}
            update={update}
            onNext={() => setStep(nextStepFrom(1))}
            jwt={jwt}
          />
        )}
        {step === 2 && (
          <Step2
            state={state}
            update={update}
            onBack={() => setStep(prevStepFrom(2))}
            onNext={advanceFromStep2}
          />
        )}
        {step === 3 && (
          <Step3
            state={state}
            update={update}
            onBack={() => setStep(prevStepFrom(3))}
            onNext={() => setStep(nextStepFrom(3))}
            onSkip={() => { setState((prev) => ({ ...prev, skippedSteps: [...prev.skippedSteps, 3] })); setStep(4); }}
            jwt={jwt}
          />
        )}
        {step === 4 && (isParametric ? (
          <Step4Parametric
            state={state}
            update={update}
            onBack={() => setStep(prevStepFrom(4))}
            onNext={() => setStep(nextStepFrom(4))}
          />
        ) : (
          <Step4
            state={state}
            update={update}
            onBack={() => setStep(prevStepFrom(4))}
            onNext={() => setStep(nextStepFrom(4))}
            onSkip={() => { setState((prev) => ({ ...prev, skippedSteps: [...prev.skippedSteps, 4] })); setStep(nextStepFrom(4)); }}
            jwt={jwt}
          />
        ))}
        {step === 5 && (
          <Step5Configuration
            state={state}
            update={update}
            onBack={() => setStep(prevStepFrom(5))}
            onNext={advanceFromStep5}
            onSkip={() => { setState((prev) => ({ ...prev, skippedSteps: [...prev.skippedSteps, 5] })); setStep(6); }}
            jwt={jwt}
          />
        )}
        {step === 6 && (
          <Step6Photos
            state={state}
            setState={setState}
            onBack={() => setStep(prevStepFrom(6))}
            onNext={advanceFromStep6}
            onSkip={() => { setState((prev) => ({ ...prev, skippedSteps: [...prev.skippedSteps, 6] })); setStep(7); }}
            jwt={jwt}
          />
        )}
        {step === 7 && (
          <Step7Review
            state={state}
            update={update}
            onBack={() => setStep(prevStepFrom(7))}
            onPublish={handlePublish}
            publishing={publishing}
            published={published}
            jwt={jwt}
            editProductId={editProductId}
            onGoToStep1={() => setStep(1)}
          />
        )}
      </div>

      {/* Bulk import modal — covers the full viewport */}
      <BulkImportModal
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
      />
    </div>
  );
}
